import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import NumberFlow from '@number-flow/react';
import {
  Clock, Users, Wallet, Receipt, Landmark, UserCog, Download, FileSpreadsheet, Lock, Eye, EyeOff,
  ShieldCheck, Plus, Ban, BadgeCheck, ChevronLeft, ChevronRight, ChevronDown, UploadCloud, AlertTriangle,
  Loader2, History, Pencil, Layers, ListChecks, ArrowRight,
} from 'lucide-react';
import api from '../../../utils/api';
import {
  PageHeader, Panel, Money, Banner, Empty, LoadingBlock, ErrorBlock, Button, Tabs, TabPanel, Segmented, Sheet, Modal,
  ReasonModal, Field, Input, Select, Switch, Checkbox, Tip, DataTable, SelectionBar, Facts, Steps, Avatar, Pill, SearchInput,
  RowMenu, useLoad, downloadFile, errMsg, fmtDate, fmtDateTime, todayIso, hoursOf, money, words, tone,
} from '../kit';
import { flagText } from './TimeEntryForm';
import { LockBadge, lockText } from './timeLock';
import { useLogTime, useOnTimeSaved } from './LogTimeProvider';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// SC hours and pay.
//
// Company level, ScHours(): three views, shown by SC permission from /sc/me (the server checks
// every call again):
//   My hours  (log_time)   this pay period's saved hours, entries grouped by day.
//   Team      (view_all_hours, generate_pay_files)
//                           one row per coordinator with their pay status; each row opens to hours
//                           by participant.
//   Pay run   (generate_pay_files)
//                           1 Contractor bills → 2 Payment file (ABA) → 3 Payroll for employees,
//                           all built from saved hours.
// Saving is final (owner spec 26 Oct 2026): no submit, approve, return or unlock. Pay statuses are
// Not paid, Billed (contractors) or Exported to payroll (employees), Paid. Flags are warnings only.
// A padlock marks an entry whose time is locked (claim file or invoice downloaded or sent, or pay
// batch paid).
// Coordinator profiles, rates and bank details live in Settings › Coordinators (CoordinatorsPanel,
// exported below). The profile still opens here as a sheet from ?coordinator= (the bank-change
// notification links to it).
//
// Client level, ParticipantHours(): a participant's time entries with totals for the pay period,
// the plan period and by coordinator and activity type.
//
// Address: ?period=YYYY-MM-DD picks the pay period that contains that day; ?hours_tab=mine|team|pay
// keeps the open view (older values: company/approvals → team, bills/payments/payroll → pay);
// ?coordinator=<user id> opens a profile. Every other param is left alone.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

const TEAM = ['view_all_hours', 'generate_pay_files'];
const HOURS_ANY = ['log_time', ...TEAM];
const PROFILE_PERMS = ['admin', 'bank_details', 'generate_pay_files', 'view_all_hours'];
// Saved hours not yet billed, exported or paid ('submitted' and 'approved' only survive on old rows).
const NOT_PAID = ['draft', 'submitted', 'approved'];
const PAY_SENT = ['billed', 'exported', 'paid'];
const notPaid = (status) => !status || NOT_PAID.includes(status);
const EMPTY = [];
const PERIOD_PARAMS = { count: 12 };
const UNPAID = { unpaid: '1' };
const ALL_STAFF = { all: '1' };
const TAB_ALIAS = { company: 'team', approvals: 'team', bills: 'pay', payments: 'pay', payroll: 'pay' };

// Pay statuses. Colour is semantic: blue not paid yet, violet in a bill or payroll, emerald paid.
// Every pill carries its word too.
const HOURS_LABEL = { draft: 'Not paid', submitted: 'Not paid', approved: 'Not paid', billed: 'Billed', exported: 'Exported to payroll', paid: 'Paid' };
const HOURS_TONE = { draft: 'blue', submitted: 'blue', approved: 'blue', billed: 'violet', exported: 'violet', paid: 'emerald' };
const BILL_STATUS = { draft: ['Draft', 'slate'], exported: ['CSV downloaded', 'violet'], in_xero: ['In Xero (draft)', 'violet'], paid: ['Paid', 'emerald'] };
const BATCH_STATUS = { generated: ['Waiting to be marked paid', 'blue'], paid: ['Paid', 'emerald'], void: ['Void', 'slate'] };
const FLAG_SHORT = { overlap: 'Overlaps another entry', daily_hours: 'Over the daily hours limit', outside_plan: 'Outside the plan dates', outside_agreement: 'Outside the service agreement' };
const flagShort = (f) => FLAG_SHORT[f.code] || flagText(f);

// ── Small formatters ─────────────────────────────────────────────────────────────────────────
const utcDate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null;
};
const shortDate = (d, year) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' });
// "12–25 Oct 2026", "28 Sep – 11 Oct 2026", "29 Dec 2025 – 11 Jan 2026": short enough for one line on a phone.
const rangeText = (start, end) => {
  const a = utcDate(start); const b = utcDate(end || start);
  if (!a || !b) return '—';
  if (a.getUTCFullYear() !== b.getUTCFullYear()) return `${shortDate(a, true)} – ${shortDate(b, true)}`;
  if (a.getUTCMonth() !== b.getUTCMonth()) return `${shortDate(a)} – ${shortDate(b, true)}`;
  return `${a.getUTCDate()}–${shortDate(b, true)}`;
};
const periodText = (p) => (p && p.start ? rangeText(p.start, p.end) : '—');
const dayLabel = (iso) => { const d = utcDate(iso); return d ? d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' }) : '—'; };
const h2 = (n) => (Number(n) || 0).toFixed(2);
const hrs = (n) => `${h2(n)} h`;
const signedHours = (mins) => { const n = Number(mins) || 0; return `${n < 0 ? '−' : ''}${hoursOf(Math.abs(n))}`; };
const timeText = (e) => (e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : 'No times');
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const rateText = (list) => ((list || EMPTY).length ? list.map((v) => `${money(v)}/h`).join(', ') : '—');
const nameParts = (n) => { const parts = String(n || '?').trim().split(/\s+/); return { first: parts[0], last: parts.length > 1 ? parts[parts.length - 1] : '' }; };

// Re-run a loader when the page says something changed (a bill, a payment, a save).
function useRefreshOn(key, reload) {
  const last = useRef(key);
  useEffect(() => {
    if (key !== last.current) { last.current = key; reload(); }
  }, [key, reload]);
}

// ── Shared pieces ────────────────────────────────────────────────────────────────────────────
const TH = 'bg-slate-50/95 text-[12px] font-semibold text-slate-600 border-y border-slate-200/80 px-3 py-2 first:pl-4 last:pr-4 text-left whitespace-nowrap';
const TD = 'px-3 first:pl-4 last:pr-4 py-2.5 border-b border-slate-100 text-slate-700 align-middle';
const FOOT = 'px-3 first:pl-4 last:pr-4 py-2.5 border-t border-slate-200 tabular-nums whitespace-nowrap';
const NUM = 'text-right tabular-nums whitespace-nowrap';

function HoursPill({ status, entries, size }) {
  if (notPaid(status) && !(Number(entries) > 0)) return <Pill status="none" label="No hours" tone="slate" size={size} />;
  const st = status || 'draft';
  return <Pill status={st} label={HOURS_LABEL[st] || words(st)} tone={HOURS_TONE[st] || 'slate'} size={size} />;
}
const EntryPill = ({ status, label }) => {
  const st = status || 'draft';
  return <Pill status={st} label={label || HOURS_LABEL[st] || words(st)} tone={HOURS_TONE[st] || 'slate'} size="xs" />;
};
const BillPill = ({ status }) => { const [l, t] = BILL_STATUS[status] || [words(status), 'slate']; return <Pill status={status} label={l} tone={t} />; };
const BatchPill = ({ status }) => { const [l, t] = BATCH_STATUS[status] || [words(status), 'slate']; return <Pill status={status} label={l} tone={t} />; };
const EngagementPill = ({ type }) => (type === 'contractor'
  ? <Pill status="contractor" label="Contractor" tone="violet" size="xs" />
  : <Pill status="employee" label="Employee" tone="blue" size="xs" />);

function FlagChips({ flags }) {
  const list = flags || EMPTY;
  if (!list.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {list.map((f) => (
        <span key={f.code} title={flagText(f)}>
          <Pill tone="amber" size="xs" label={flagShort(f)} />
        </span>
      ))}
    </span>
  );
}

// A compact strip of figures (never a wall of equal tiles). items: [{ label, value, tone }].
function Strip({ items, className }) {
  return (
    <dl className={clsx('flex flex-wrap gap-x-7 gap-y-3', className)}>
      {items.filter(Boolean).map((it) => (
        <div key={it.label} className="min-w-0">
          <dt className="text-[12px] font-medium text-slate-500">{it.label}</dt>
          <dd className={clsx('mt-0.5 text-[15px] font-semibold tabular-nums', it.tone ? tone(it.tone).ink : 'text-slate-900')}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function IconBtn({ label, icon: I, onClick, disabled }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className="h-11 w-11 sm:h-9 sm:w-9 flex-shrink-0 inline-flex items-center justify-center rounded-xl bg-white text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      <I size={15} aria-hidden />
    </button>
  );
}

// The keyboard way into a clickable row: a real button.
function RowLink({ onClick, children, label, busy }) {
  return (
    <button type="button" aria-label={label} onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center gap-1.5 text-left font-semibold text-slate-900 hover:text-indigo-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      {children}
      {busy && <Loader2 size={13} className="animate-spin text-slate-500" aria-hidden />}
    </button>
  );
}

function Warnings({ list }) {
  if (!list || !list.length) return null;
  return (
    <Tip content={<ul className="space-y-1">{list.map((w) => <li key={w}>{w}</li>)}</ul>}>
      <button type="button" onClick={(e) => e.stopPropagation()} aria-label={`${plural(list.length, 'set-up problem')}: ${list.join(' ')}`}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-semibold text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        <AlertTriangle size={12} aria-hidden />{plural(list.length, 'set-up problem')}
      </button>
    </Tip>
  );
}

function SectionTitle({ icon: I, children, action }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
      <h3 className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-1.5">{I && <I size={14} className="text-violet-600" aria-hidden />}{children}</h3>
      {action}
    </div>
  );
}

// An action that can't run says why, next to it, instead of being a grey button with no reason.
let gateSeq = 0;
function GatedButton({ reason, children, align = 'end', ...p }) {
  const [id] = useState(() => `sch-gate-${++gateSeq}`);
  return (
    <div className={clsx('flex flex-col gap-1', align === 'end' ? 'items-stretch sm:items-end' : 'items-start')}>
      <Button {...p} disabled={!!reason || p.disabled} aria-describedby={reason ? id : undefined}>{children}</Button>
      {reason && <p id={id} className={clsx('text-[12px] text-slate-500 max-w-xs', align === 'end' && 'sm:text-right')}>{reason}</p>}
    </div>
  );
}

// Pay period: earlier / pick / later on one line, also at phone width.
function PeriodPicker({ id, periods, value, onChange, showMine, hideLabel }) {
  const list = periods?.data?.periods || EMPTY;
  const options = list.map((p) => ({
    value: p.start,
    label: `${periodText(p)}${p.current ? ' · current' : ''}`,
    hint: showMine && p.my_status ? `Your hours: ${String(p.my_status_label || HOURS_LABEL[p.my_status] || 'Not paid').toLowerCase()}` : undefined,
  }));
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: `Pay period with ${fmtDate(value)}` });
  const i = list.findIndex((p) => p.start === value);
  const older = i >= 0 && i < list.length - 1 ? list[i + 1].start : null;
  const newer = i > 0 ? list[i - 1].start : null;
  return (
    <div className="flex items-center gap-1.5 min-w-0 w-full sm:w-auto">
      <label htmlFor={id} className={clsx('text-[12.5px] font-semibold text-slate-600 mr-1 whitespace-nowrap', hideLabel ? 'sr-only' : 'max-sm:sr-only')}>Pay period</label>
      <IconBtn label="Earlier pay period" icon={ChevronLeft} disabled={!older} onClick={() => onChange(older)} />
      <div className="flex-1 min-w-0 sm:flex-none sm:w-[248px]">
        <Select id={id} value={value || ''} onChange={onChange} options={options}
          className="[&>span:first-child]:min-w-0 [&>span:first-child]:truncate"
          placeholder={periods?.loading ? 'Loading pay periods…' : 'Choose a pay period'} />
      </div>
      <IconBtn label="Later pay period" icon={ChevronRight} disabled={!newer} onClick={() => onChange(newer)} />
    </div>
  );
}

// A short decision. Errors stay inside the dialog, next to the button that caused them.
function DecisionModal({ open, onClose, title, sub, children, confirmLabel = 'Confirm', variant = 'primary', icon, onConfirm, disabled, width = 'sm' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) setError(''); }, [open]);
  const go = async () => {
    setBusy(true); setError('');
    try { await onConfirm(); onClose(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={title} sub={sub} width={width}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button variant={variant} icon={icon} loading={busy} disabled={disabled} onClick={go}>{confirmLabel}</Button></>}>
      <div className="space-y-4">
        {children}
        {error && <Banner tone="red" title="That didn't work">{error}</Banner>}
      </div>
    </Modal>
  );
}

// result: { title, sub, groups: [{ tone, title, items: [{ key, primary, secondary }] }] }
function ResultsModal({ result, onClose }) {
  const groups = (result?.groups || EMPTY).filter((g) => g.items.length);
  return (
    <Modal open={!!result} onClose={onClose} title={result?.title || ''} sub={result?.sub} width="lg" footer={<Button onClick={onClose}>Done</Button>}>
      {!groups.length ? <p className="text-sm text-slate-500">Nothing to report.</p> : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.title}>
              <h3 className="flex items-center gap-2 text-[13px] font-semibold text-slate-900 mb-2"><Pill tone={g.tone} size="xs" label={String(g.items.length)} />{g.title}</h3>
              <ul className="divide-y divide-slate-100 border-y border-slate-100">
                {g.items.map((it, i) => (
                  <li key={it.key || i} className="py-2.5">
                    <p className="text-[13px] font-semibold text-slate-900">{it.primary}</p>
                    {it.secondary && <p className="text-[12.5px] text-slate-600 mt-0.5">{it.secondary}</p>}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

// Opens a time entry in the one Log time sheet (LogTimeProvider). The hours endpoints don't return
// the engagement id, so the entry is read from /sc/time-entries/:id. An adjustment opens the entry
// it corrects, where its adjustments are listed. A save fires 'sc:time-saved'; lists refresh on it.
function useEntryEditor({ coordinationId, budgets, services, participantName } = {}) {
  const logTime = useLogTime();
  const [busyId, setBusyId] = useState(null);
  const open = useCallback(async (row) => {
    if (!row || !row.id) return;
    setBusyId(row.id);
    try {
      const first = row.source === 'adjustment' && row.adjusts_entry_id ? row.adjusts_entry_id : row.id;
      let e = (await api.get(`/sc/time-entries/${first}`)).data;
      if (e.source === 'adjustment' && e.adjusts_entry_id) e = (await api.get(`/sc/time-entries/${e.adjusts_entry_id}`)).data;
      // The flags saved with an entry go stale when another entry moves; the hours rows carry the
      // flags worked out now, so the form shows those (warnings only).
      if (e.id === row.id && Array.isArray(row.flags)) e = { ...e, flags: row.flags };
      const coord = e.coordination_id || coordinationId || null;
      let b = budgets || null;
      let s = services || null;
      if ((!b || !s) && coord && !e.locked) {
        try {
          const eng = (await api.get(`/support-coordination/${coord}`)).data || {};
          b = b || eng.budgets || EMPTY; s = s || eng.services || EMPTY;
        } catch { /* the form still keeps the entry's own budget and service */ }
      }
      const name = participantName || `${e.client_first || ''} ${e.client_last || ''}`.trim() || row.client_name || undefined;
      logTime({ entry: e, coordinationId: coord, budgets: b || EMPTY, services: s || EMPTY, participantName: name });
    } catch (err) {
      toast.error(errMsg(err, 'Could not open this time entry.'));
    } finally { setBusyId(null); }
  }, [logTime, coordinationId, budgets, services, participantName]);
  return { open, busyId };
}

// Entries grouped by day: the day's total leads, then each entry as one tappable row (no table,
// so nothing scrolls sideways on a phone).
function DayEntries({ entries, onOpen, busyId, showPay, empty }) {
  const days = useMemo(() => {
    const map = new Map();
    for (const e of entries || EMPTY) {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    }
    return [...map.entries()].map(([date, list]) => ({
      date, list,
      minutes: list.reduce((t, e) => t + (Number(e.minutes) || 0), 0),
      overDaily: list.some((e) => (e.flags || EMPTY).some((f) => f.code === 'daily_hours')),
    }));
  }, [entries]);
  if (!days.length) return empty || <Empty icon={Clock} title="No time entries" />;
  return (
    <div>
      {days.map((g) => (
        <section key={g.date} aria-label={`${dayLabel(g.date)}, ${signedHours(g.minutes)} hours`}>
          <h3 className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 border-y border-slate-100 px-5 py-2">
            <span className="text-[12.5px] font-semibold text-slate-800">{dayLabel(g.date)}</span>
            <span className="flex items-center gap-2 text-[12px] text-slate-600 tabular-nums font-normal">
              {g.overDaily && <Pill tone="amber" size="xs" label="Over the daily hours limit" />}
              {plural(g.list.length, 'entry', 'entries')} · <span className="font-semibold text-slate-900">{signedHours(g.minutes)} h</span>
            </span>
          </h3>
          <ul className="divide-y divide-slate-100">
            {g.list.map((e) => <EntryRow key={e.id} e={e} onOpen={onOpen} busy={busyId === e.id} showPay={showPay} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}

function EntryRow({ e, onOpen, busy, showPay }) {
  const adj = e.source === 'adjustment';
  const openFlags = (e.flags || EMPTY).length > 0;
  const label = `Open ${adj ? 'adjustment' : 'time entry'}: ${e.client_name || 'participant'}, ${dayLabel(e.date)}${adj ? '' : `, ${timeText(e)}`}${e.locked ? `. Time locked: ${lockText(e)}` : ''}`;
  return (
    <li>
      <button type="button" onClick={() => onOpen(e)} aria-label={label}
        className={clsx('w-full text-left px-5 py-3 flex items-start gap-3 transition-colors hover:bg-indigo-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500',
          openFlags && 'bg-amber-50/40')}>
        <span className="w-[86px] sm:w-[96px] flex-shrink-0 text-[13px] font-semibold text-slate-900 tabular-nums">
          {adj ? 'Adjustment' : timeText(e)}
          {busy && <Loader2 size={13} className="inline ml-1 animate-spin text-slate-500" aria-hidden />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] text-slate-900 truncate">{e.client_name || '—'}</span>
          <span className="block text-[12.5px] text-slate-500">
            {e.activity || 'No activity type'} · {e.billable ? 'Billable' : 'Non-billable'}
            {e.activity_paid === false ? ' · unpaid activity' : ''}
            {showPay && e.pay ? ` · ${money(e.pay.amount)} pay` : ''}
          </span>
          {adj && e.adjustment_reason && <span className="block text-[12px] text-slate-500 truncate">{e.adjustment_reason}</span>}
          {(e.flags || EMPTY).length > 0 && <span className="mt-1.5 block"><FlagChips flags={e.flags} /></span>}
        </span>
        <span className="flex-shrink-0 flex flex-col items-end gap-1">
          <span className={clsx('text-[13.5px] font-semibold tabular-nums', Number(e.minutes) < 0 ? 'text-rose-600' : 'text-slate-900')}>{signedHours(e.minutes)} h</span>
          <span className="inline-flex items-center gap-1">
            {e.locked && <span title={lockText(e)} className="inline-flex"><Lock size={12} className="text-violet-500" aria-hidden /></span>}
            <EntryPill status={e.pay_status} label={e.pay_status_label} />
          </span>
        </span>
      </button>
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Company level page
// ═══════════════════════════════════════════════════════════════════════════════════════════════
export default function ScHours() {
  const [sp, setSp] = useSearchParams();
  const logTime = useLogTime();
  const me = useLoad('/sc/me');
  const perms = useMemo(() => me.data?.permissions || EMPTY, [me.data]);
  const has = useCallback((...keys) => keys.some((k) => perms.includes(k)), [perms]);
  const canHours = has(...HOURS_ANY);
  const periods = useLoad('/sc/hours/periods', PERIOD_PARAMS, { skip: !canHours });
  const { reload: reloadPeriods } = periods;

  const setParam = useCallback((key, value) => {
    setSp((p) => {
      const n = new URLSearchParams(p);
      if (value) n.set(key, value); else n.delete(key);
      return n;
    }, { replace: true });
  }, [setSp]);

  // ?period= may be any day; it selects the pay period that contains it.
  const rawPeriod = sp.get('period') || '';
  const periodStart = useMemo(() => {
    if (!periods.data) return periods.error ? (rawPeriod || todayIso()) : '';
    const list = periods.data.periods || EMPTY;
    if (!rawPeriod) return (list.find((p) => p.current) || list[0] || {}).start || todayIso();
    const hit = list.find((p) => p.start <= rawPeriod && rawPeriod <= p.end);
    return hit ? hit.start : rawPeriod;
  }, [rawPeriod, periods.data, periods.error]);
  const setPeriod = useCallback((start) => { if (start) setParam('period', start); }, [setParam]);

  const tabs = useMemo(() => [
    has('log_time') && { value: 'mine', label: 'My hours', icon: Clock },
    has(...TEAM) && { value: 'team', label: 'Team', icon: Users },
    has('generate_pay_files') && { value: 'pay', label: 'Pay run', icon: Wallet },
  ].filter(Boolean), [has]);
  const rawTab = sp.get('hours_tab');
  const tabParam = TAB_ALIAS[rawTab] || rawTab;
  const tab = tabs.some((t) => t.value === tabParam) ? tabParam : (tabs[0] ? tabs[0].value : '');
  const on = (v) => tabs.some((t) => t.value === v);
  const goTab = useCallback((v) => setParam('hours_tab', v), [setParam]);

  const [sheet, setSheet] = useState(null);          // one coordinator's pay period
  const [bump, setBump] = useState(0);
  const changed = useCallback(() => { setBump((b) => b + 1); reloadPeriods(); }, [reloadPeriods]);
  useOnTimeSaved(changed);

  const coordinatorId = sp.get('coordinator') || '';
  const openProfile = useCallback((id) => setParam('coordinator', id), [setParam]);
  const closeProfile = useCallback(() => setParam('coordinator', ''), [setParam]);
  const canProfiles = has(...PROFILE_PERMS);
  const shared = { periodStart, periods, onPeriod: setPeriod, bump, has, onChanged: changed };
  // Keyed so a newly opened record never shows the last one's data while it loads.
  const sheetKey = `period:${sheet ? `${sheet.userId}:${sheet.periodStart || ''}` : 'closed'}`;

  return (
    <div>
      <PageHeader title="SC hours"
        sub="Saved time counts straight away and is paid by bill or payroll. Locked time is corrected with an adjustment."
        actions={has('log_time') && <Button variant="secondary" icon={Plus} onClick={() => logTime()}>Log time</Button>} />
      {me.error ? <ErrorBlock error={me.error} onRetry={me.reload} /> : !me.data ? <LoadingBlock rows={6} /> : !tabs.length ? (
        <Panel>
          <Empty icon={Lock} title="You don't have access to SC hours">
            Ask an admin for the Log time, View all hours or Generate pay files permission.
          </Empty>
        </Panel>
      ) : (
        <Tabs value={tab} onChange={goTab} tabs={tabs} label="SC hours">
          {on('mine') && <TabPanel value="mine"><MyHours {...shared} /></TabPanel>}
          {on('team') && (
            <TabPanel value="team">
              <TeamHours {...shared} onOpen={setSheet}
                onOpenProfile={canProfiles ? openProfile : undefined} onPayRun={on('pay') ? () => goTab('pay') : undefined} />
            </TabPanel>
          )}
          {on('pay') && <TabPanel value="pay"><PayRun {...shared} onOpenProfile={canProfiles ? openProfile : undefined} /></TabPanel>}
        </Tabs>
      )}
      <PeriodSheet key={sheetKey} target={sheet} onClose={() => setSheet(null)} onOpenProfile={canProfiles ? openProfile : undefined} />
      <ProfileSheet key={`profile:${coordinatorId || 'closed'}`} userId={coordinatorId} onClose={closeProfile} has={has} meId={me.data?.userId} onChanged={changed} />
    </div>
  );
}

// ── My hours (log_time): this period's saved hours; saving is final, nothing to send on ─────────
function MyHours({ periodStart, periods, onPeriod, bump }) {
  const logTime = useLogTime();
  const d = useLoad('/sc/hours/my', { period_start: periodStart }, { skip: !periodStart });
  useRefreshOn(bump, d.reload);
  const { reload } = d;
  const editor = useEntryEditor();
  const data = d.data;
  const sum = data?.summary || {};
  const flags = data?.flags?.items || EMPTY;
  const pay = data?.approved_pay || (data ? { pay_ex_gst: sum.pay_ex_gst, gst: sum.gst, pay_total: sum.pay_total } : null);
  const entries = data?.entries || EMPTY;
  const days = useMemo(() => new Set(entries.map((e) => e.date)).size, [entries]);
  const billablePct = sum.total_minutes > 0 ? Math.round((sum.billable_minutes / sum.total_minutes) * 100) : 0;
  const hasEntries = Number(sum.entries) > 0;
  const lockedCount = entries.filter((e) => e.locked).length;

  const openFlagged = (f) => {
    const e = entries.find((x) => x.id === f.entry_id);
    if (e) editor.open(e);
  };

  let helper = '';
  if (data) {
    if (notPaid(data.status)) helper = hasEntries ? 'Saved hours count straight away. They are paid in the next bill or payroll export.' : 'Time you log for a participant shows here.';
    else if (data.status === 'billed') helper = data.bill ? `Billed on ${data.bill.bill_number}.` : 'Billed.';
    else if (data.status === 'exported') helper = 'Sent to payroll.';
    else if (data.status === 'paid') helper = 'Paid.';
  }

  return (
    <div className="space-y-5">
      <PeriodPicker id="sch-period-mine" periods={periods} value={periodStart} onChange={onPeriod} showMine />
      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !data ? <LoadingBlock rows={6} /> : (
        <>
          {(sum.missing_rate || EMPTY).length > 0 && (
            <Banner tone="amber" title="No pay rate for some days">
              {sum.missing_rate.map((m) => `${fmtDate(m.date)} (${plural(m.entries, 'entry', 'entries')})`).join(', ')}. Ask your admin to add a rate to your profile. The pay estimate leaves these hours out.
            </Banner>
          )}

          <section aria-labelledby="sch-mine-total" className="rounded-2xl bg-white border border-slate-200/80 p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="min-w-0">
                <h2 id="sch-mine-total" className="text-[13px] font-semibold text-violet-700">Your hours · {periodText(data.period)}</h2>
                <p className="mt-1.5 flex items-baseline gap-2">
                  <span className="text-[44px] leading-none font-semibold tabular-nums tracking-[-0.03em] text-slate-900">
                    <NumberFlow value={Number(sum.total_hours) || 0} locales="en-AU" format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                  </span>
                  <span className="text-[15px] font-medium text-slate-500">hours</span>
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[12.5px] text-slate-500">
                  <HoursPill status={data.status} entries={sum.entries} />
                  {lockedCount > 0 && <span className="inline-flex items-center gap-1"><Lock size={12} className="text-violet-500" aria-hidden />{plural(lockedCount, 'entry', 'entries')} with locked time</span>}
                </div>
              </div>
              <p className="text-[12.5px] text-slate-500 max-w-xs sm:text-right">{helper}</p>
            </div>
            {hasEntries && (
              <Strip className="mt-5 pt-4 border-t border-slate-100" items={[
                { label: 'Billable', value: `${hrs(sum.billable_hours)} · ${billablePct}%` },
                { label: 'Non-billable', value: hrs(sum.nonbillable_hours) },
                { label: 'Entries', value: `${sum.entries} over ${plural(days, 'day')}` },
                flags.length
                  ? { label: 'Warnings', value: plural(flags.length, 'warning'), tone: 'amber' }
                  : { label: 'Warnings', value: 'None', tone: 'emerald' },
              ]} />
            )}
          </section>

          {flags.length > 0 && (
            <Banner tone="amber" title={`${plural(flags.length, 'warning')} on your hours`}>
              <ul className="mt-1 space-y-1">
                {flags.map((f) => (
                  <li key={`${f.entry_id}-${f.code}`}>
                    <button type="button" onClick={() => openFlagged(f)}
                      className="text-left underline decoration-amber-400 underline-offset-2 hover:decoration-amber-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                      {fmtDate(f.note_date)}{f.client_name ? `, ${f.client_name}` : ''}: {flagText(f)}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5">For information only: the hours are saved and count. Time outside the plan or service agreement dates can’t be claimed.</p>
            </Banner>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
            <Panel title="Time entries" sub="Choose an entry to open it. A padlock means the time is locked; correct it with Add adjustment. Case notes stay editable." icon={Clock} pad={false}>
              <div className="pb-2">
                <DayEntries entries={entries} onOpen={editor.open} busyId={editor.busyId}
                  empty={(
                    <Empty icon={Clock} title="No time in this pay period"
                      action={<Button icon={Plus} onClick={() => logTime()}>Log time</Button>}>
                      Log time after a call, meeting or task and it shows here as soon as you save it.
                    </Empty>
                  )} />
              </div>
            </Panel>
            <div className="space-y-5">
              <Panel title={data.approved_pay ? 'Pay for this period' : 'Pay estimate'} icon={Wallet} hue="emerald"
                sub={data.pay_path === 'bill' ? 'Contractor: paid by bill and bank transfer.' : 'Employee: paid through payroll, which works out the final pay.'}>
                <dl className="space-y-2 text-[13px]">
                  <PayRow k="Paid hours" v={hrs(sum.paid_hours)} />
                  <PayRow k="Rate" v={rateText(sum.rates_used)} />
                  <PayRow k="Pay excluding GST" v={money(pay?.pay_ex_gst)} />
                  <PayRow k="GST" v={money(pay?.gst)} />
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                    <dt className="font-semibold text-slate-900">Total</dt>
                    <dd className="text-[18px] font-semibold tabular-nums text-emerald-700">{money(pay?.pay_total)}</dd>
                  </div>
                </dl>
              </Panel>
              {(data.by_client || EMPTY).length > 0 && (
                <Panel title="By participant" icon={Users}>
                  <ul className="space-y-2">
                    {data.by_client.map((c) => (
                      <li key={c.client_id || c.client_name} className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="truncate text-slate-700">{c.client_name}</span>
                        <span className="tabular-nums text-slate-500 whitespace-nowrap"><span className="font-semibold text-slate-900">{hrs(c.hours)}</span> · {h2(c.billable_hours)} billable</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const PayRow = ({ k, v }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-slate-500">{k}</dt>
    <dd className="tabular-nums text-slate-800 text-right">{v}</dd>
  </div>
);

// ── Team: one row per coordinator for the period, with their pay status ──────────────────────
// Not paid yet first, then billed, exported or paid, then no hours; each group by name.
const RANK = (r) => {
  if (notPaid(r.status) && r.entries > 0) return 0;
  if (PAY_SENT.includes(r.status)) return 1;
  return 2;
};
const flagCount = (r) => Number(r.flag_count ?? r.unresolved_flag_count) || 0;

function FilterChip({ active, onClick, label, count }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick}
      className={clsx('inline-flex items-center gap-1.5 rounded-full px-3 h-10 sm:h-8 text-[12.5px] font-semibold ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        active ? 'bg-violet-50 text-violet-800 ring-violet-200' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50')}>
      {label}<span className="tabular-nums text-slate-500">{count}</span>
    </button>
  );
}

function ByClient({ list, id }) {
  const rows = list || EMPTY;
  if (!rows.length) return <p id={id} className="text-[12.5px] text-slate-500">No time in this pay period.</p>;
  return (
    <table id={id} className="w-full text-[12.5px]">
      <caption className="sr-only">Hours by participant</caption>
      <thead>
        <tr className="text-slate-600">
          <th scope="col" className="text-left font-semibold py-1 pr-3">Participant</th>
          <th scope="col" className="text-right font-semibold py-1 px-2">Hours</th>
          <th scope="col" className="text-right font-semibold py-1 px-2 max-sm:hidden">Billable</th>
          <th scope="col" className="text-right font-semibold py-1 px-2 max-sm:hidden">Non-billable</th>
          <th scope="col" className="text-right font-semibold py-1 pl-2">Pay ex GST</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.client_id || c.client_name} className="border-t border-slate-200/70">
            <td className="py-1.5 pr-3 text-slate-800">{c.client_name}</td>
            <td className="py-1.5 px-2 text-right tabular-nums font-semibold text-slate-900">{hrs(c.hours)}</td>
            <td className="py-1.5 px-2 text-right tabular-nums max-sm:hidden">{hrs(c.billable_hours)}</td>
            <td className="py-1.5 px-2 text-right tabular-nums max-sm:hidden">{hrs(c.nonbillable_hours)}</td>
            <td className="py-1.5 pl-2 text-right tabular-nums">{money(c.pay_ex_gst)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TeamHours({ periodStart, periods, onPeriod, bump, onOpen, onOpenProfile, onPayRun }) {
  const d = useLoad('/sc/hours/company', { period_start: periodStart }, { skip: !periodStart });
  useRefreshOn(bump, d.reload);
  const { reload } = d;
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const data = d.data;
  const rows = data?.rows || EMPTY;
  const sorted = useMemo(() => [...rows].sort((a, b) => RANK(a) - RANK(b) || String(a.name).localeCompare(String(b.name))), [rows]);
  const groups = useMemo(() => ({
    notpaid: sorted.filter((r) => notPaid(r.status) && r.entries > 0),
    sent: sorted.filter((r) => PAY_SENT.includes(r.status)),
  }), [sorted]);
  const shown = filter === 'all' ? sorted : (groups[filter] || sorted);
  const t = data?.totals || {};
  const pStart = data?.period?.start;

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const openRow = (r) => onOpen({ userId: r.user_id, periodStart: pStart });

  const exportXlsx = async () => {
    setExporting(true);
    try { const name = await downloadFile('/sc/hours/company/export', { params: { period_start: periodStart } }); toast.success(`Downloaded ${name}`); }
    catch (e) { toast.error(errMsg(e, 'Could not export the hours.')); } finally { setExporting(false); }
  };

  const menu = (r) => (
    <RowMenu label={`More for ${r.name}`} items={[
      { label: 'Open hours', icon: Clock, onClick: () => openRow(r) },
      onOpenProfile && { label: 'Profile and rates', icon: UserCog, onClick: () => onOpenProfile(r.user_id) },
    ]} />
  );
  const flagsNote = (r) => {
    const n = flagCount(r);
    return n ? <Pill tone="amber" size="xs" label={plural(n, 'warning')} /> : null;
  };
  const statusOf = (r) => <HoursPill status={r.status} entries={r.entries} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker id="sch-period-team" periods={periods} value={periodStart} onChange={onPeriod} />
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" icon={FileSpreadsheet} loading={exporting} disabled={!periodStart} onClick={exportXlsx}>Export to Excel</Button>
          {onPayRun && <Button size="sm" variant="ghost" icon={ArrowRight} onClick={onPayRun}>Pay run</Button>}
        </div>
      </div>

      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !data ? <LoadingBlock rows={6} /> : (
        <Panel pad={false}>
          <div className="px-5 pt-4 pb-3 space-y-4">
            <Strip items={[
              { label: 'Hours', value: hrs(t.total_hours) },
              { label: 'Billable', value: hrs(t.billable_hours) },
              { label: 'Non-billable', value: hrs(t.nonbillable_hours) },
              { label: 'Pay this period', value: `${money(t.pay_total)}`, tone: 'emerald' },
            ]} />
            {rows.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show coordinators">
                <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="Everyone" count={rows.length} />
                {groups.notpaid.length > 0 && <FilterChip active={filter === 'notpaid'} onClick={() => setFilter('notpaid')} label="Not paid" count={groups.notpaid.length} />}
                {groups.sent.length > 0 && <FilterChip active={filter === 'sent'} onClick={() => setFilter('sent')} label="Billed, exported or paid" count={groups.sent.length} />}
              </div>
            )}
          </div>

          {!rows.length ? (
            <Empty icon={Users} title="No support coordinators here">Coordinators show here once they have a profile or log time in this pay period.</Empty>
          ) : (
            <>
              {/* Wide screens: the table. */}
              <div className="hidden lg:block overflow-x-auto pb-1">
                <table className="w-full text-[13px] border-separate border-spacing-0">
                  <caption className="sr-only">Hours and pay for each support coordinator, {periodText(data.period)}. Not paid first.</caption>
                  <thead>
                    <tr>
                      <th scope="col" className={clsx(TH, 'w-10')}><span className="sr-only">Hours by participant</span></th>
                      <th scope="col" className={TH}>Support coordinator</th>
                      <th scope="col" className={TH}>Pay status</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Total</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Billable</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Non-billable</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Rate</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Pay ex GST</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>GST</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Total pay</th>
                      <th scope="col" className={TH}><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => {
                      const n = nameParts(r.name);
                      const isOpen = expanded.has(r.user_id);
                      return (
                        <React.Fragment key={r.user_id}>
                          <tr>
                            <td className={TD}>
                              <button type="button" onClick={() => toggle(r.user_id)} aria-expanded={isOpen} aria-controls={`sch-team-${r.user_id}-d`}
                                aria-label={`Hours by participant for ${r.name}`}
                                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                                <ChevronDown size={15} className={clsx('transition-transform', isOpen && 'rotate-180')} aria-hidden />
                              </button>
                            </td>
                            <td className={TD}>
                              <div className="flex items-center gap-2.5 min-w-[170px]">
                                <Avatar first={n.first} last={n.last} />
                                <div className="min-w-0">
                                  <RowLink onClick={() => openRow(r)}>{r.name}</RowLink>
                                  <p className="text-[12px] text-slate-500">{r.engagement_label}{r.contractor_code ? ` · ${r.contractor_code}` : ''}{r.bill ? ` · ${r.bill.bill_number}` : ''}</p>
                                </div>
                              </div>
                            </td>
                            <td className={TD}>
                              <div className="flex flex-wrap items-center gap-1.5">{statusOf(r)}{flagsNote(r)}<Warnings list={r.warnings} /></div>
                            </td>
                            <td className={clsx(TD, NUM, 'font-semibold text-slate-900')}>{hrs(r.total_hours)}</td>
                            <td className={clsx(TD, NUM)}>{hrs(r.billable_hours)}</td>
                            <td className={clsx(TD, NUM)}>{hrs(r.nonbillable_hours)}</td>
                            <td className={clsx(TD, NUM)}>{(r.missing_rate || EMPTY).length ? <Pill tone="amber" size="xs" label="Rate missing" /> : rateText(r.rates_used)}</td>
                            <td className={clsx(TD, NUM)}>{money(r.pay_ex_gst)}</td>
                            <td className={clsx(TD, NUM)}>{money(r.gst)}</td>
                            <td className={clsx(TD, NUM, 'font-semibold text-slate-900')}>{money(r.pay_total)}</td>
                            <td className={clsx(TD, 'text-right')}>
                              <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">{menu(r)}</div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td className="border-b border-slate-100" />
                              <td colSpan={10} className="bg-slate-50/70 border-b border-slate-100 px-3 py-3"><ByClient list={r.by_client} id={`sch-team-${r.user_id}-d`} /></td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {filter === 'all' && (
                    <tfoot>
                      <tr className="font-semibold text-slate-800 bg-slate-50/70">
                        <td className={FOOT} />
                        <td colSpan={2} className={clsx(FOOT, 'text-left')}>Total · {plural(rows.length, 'coordinator')}</td>
                        <td className={clsx(FOOT, 'text-right')}>{hrs(t.total_hours)}</td>
                        <td className={clsx(FOOT, 'text-right')}>{hrs(t.billable_hours)}</td>
                        <td className={clsx(FOOT, 'text-right')}>{hrs(t.nonbillable_hours)}</td>
                        <td className={FOOT} />
                        <td className={clsx(FOOT, 'text-right')}>{money(t.pay_ex_gst)}</td>
                        <td className={clsx(FOOT, 'text-right')}>{money(t.gst)}</td>
                        <td className={clsx(FOOT, 'text-right')}>{money(t.pay_total)}</td>
                        <td className={FOOT} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Phones and tablets: one card per coordinator. */}
              <ul className="lg:hidden divide-y divide-slate-100 border-t border-slate-100">
                {shown.map((r) => {
                  const n = nameParts(r.name);
                  const isOpen = expanded.has(r.user_id);
                  return (
                    <li key={r.user_id} className="px-5 py-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar first={n.first} last={n.last} />
                          <div className="min-w-0">
                            <RowLink onClick={() => openRow(r)}>{r.name}</RowLink>
                            <p className="text-[12px] text-slate-500 truncate">{r.engagement_label}{r.contractor_code ? ` · ${r.contractor_code}` : ''}</p>
                          </div>
                        </div>
                        {statusOf(r)}
                      </div>
                      <p className="mt-2 text-[13px] text-slate-600 tabular-nums">
                        <span className="font-semibold text-slate-900">{hrs(r.total_hours)}</span> · {h2(r.billable_hours)} billable · <span className="font-semibold text-slate-900">{money(r.pay_total)}</span>
                        {r.gst ? ` incl. ${money(r.gst)} GST` : ''}
                      </p>
                      {(flagsNote(r) || (r.warnings || EMPTY).length > 0) && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{flagsNote(r)}<Warnings list={r.warnings} /></div>}
                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="ghost" icon={ChevronDown} onClick={() => toggle(r.user_id)} aria-expanded={isOpen} aria-controls={`sch-team-${r.user_id}-m`}>
                          By participant
                        </Button>
                        {menu(r)}
                      </div>
                      {isOpen && <div className="mt-2.5 rounded-xl bg-slate-50 px-3 py-2.5"><ByClient list={r.by_client} id={`sch-team-${r.user_id}-m`} /></div>}
                    </li>
                  );
                })}
              </ul>
              <p className="px-5 py-3 text-[12px] text-slate-500 border-t border-slate-100">Billed, exported and paid rows show the pay as it was sent; the rest are estimates from the rates on file. Warnings are for information only.</p>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}

// ── One coordinator's pay period: saved hours, warnings, pay and entries (read only) ──────────
function PeriodSheet({ target, onClose, onOpenProfile }) {
  const url = target?.userId ? `/sc/hours/coordinators/${target.userId}/period` : null;
  const params = target?.userId && target.periodStart ? { period_start: target.periodStart } : null;
  const d = useLoad(url, params, { skip: !url });
  const { reload } = d;
  useOnTimeSaved(reload);
  const editor = useEntryEditor();
  const data = target ? d.data : null;
  const status = data?.status;
  const sum = data?.summary || {};
  const flags = data?.flags?.items || EMPTY;
  const entries = data?.entries || EMPTY;
  const pay = data?.approved_pay || { pay_ex_gst: sum.pay_ex_gst, gst: sum.gst, pay_total: sum.pay_total };
  const name = data?.coordinator?.name || 'This support coordinator';
  const lockedCount = entries.filter((e) => e.locked).length;

  return (
    <Sheet open={!!target} onClose={onClose} width="xl" icon={Clock} hue="violet"
      title={data ? name : 'Hours'} sub={data ? `${periodText(data.period)} · ${data.profile?.engagement_label || 'Employee (no profile)'}` : undefined}
      footer={(
        <>
          {onOpenProfile && data && <Button variant="ghost" icon={UserCog} onClick={() => onOpenProfile(data.coordinator.id)}>Profile and rates</Button>}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      )}>
      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !data ? <LoadingBlock rows={6} /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-500">
            <HoursPill status={status} entries={sum.entries} />
            {data.bill && <span>Bill <span className="font-mono text-violet-700">{data.bill.bill_number}</span></span>}
            {lockedCount > 0 && <span className="inline-flex items-center gap-1"><Lock size={12} className="text-violet-500" aria-hidden />{plural(lockedCount, 'entry', 'entries')} with locked time</span>}
          </div>
          {(sum.missing_rate || EMPTY).length > 0 && (
            <Banner tone="amber" title="No pay rate for some days">
              {sum.missing_rate.map((m) => fmtDate(m.date)).join(', ')}.{' '}
              {data.pay_path === 'bill' ? 'Add a rate to their profile, then generate the bill.' : 'The hours still go to payroll; the pay estimate leaves them out.'}
            </Banner>
          )}

          <Strip items={[
            { label: 'Hours', value: `${hrs(sum.total_hours)} · ${plural(Number(sum.entries) || 0, 'entry', 'entries')}` },
            { label: 'Billable', value: hrs(sum.billable_hours) },
            { label: 'Non-billable', value: hrs(sum.nonbillable_hours) },
            { label: data.approved_pay ? 'Pay for this period' : 'Pay estimate', value: money(pay.pay_total), tone: 'emerald' },
            { label: 'Rate', value: rateText(sum.rates_used) },
            { label: 'Paid by', value: data.pay_path === 'bill' ? 'Bill and ABA file' : 'Payroll' },
          ]} />

          {flags.length > 0 && (
            <section>
              <SectionTitle icon={AlertTriangle}>Warnings</SectionTitle>
              <p className="text-[12.5px] text-slate-500 mb-2">For information only. The hours are saved and count. Time outside the plan or service agreement dates can’t be claimed.</p>
              <ul className="divide-y divide-slate-100 border-y border-slate-100">
                {flags.map((f) => (
                  <li key={`${f.entry_id}-${f.code}`} className="py-2.5">
                    <p className="text-[13px] font-semibold text-slate-900">{flagShort(f)} · {fmtDate(f.note_date)}{f.client_name ? ` · ${f.client_name}` : ''}</p>
                    <p className="text-[12.5px] text-slate-600 mt-0.5">{flagText(f)}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(data.by_client || EMPTY).length > 0 && (
            <section>
              <SectionTitle icon={Users}>Hours by participant</SectionTitle>
              <ByClient list={data.by_client} id="sch-sheet-by-client" />
            </section>
          )}

          <section className="-mx-6">
            <div className="px-6"><SectionTitle icon={Clock}>Time entries</SectionTitle></div>
            <DayEntries entries={entries} onOpen={editor.open} busyId={editor.busyId} showPay
              empty={<Empty icon={Clock} title="No time entries in this pay period" />} />
          </section>
        </div>
      )}
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Pay run (generate_pay_files): 1 Contractor bills → 2 Payment file → 3 Payroll for employees
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const STEP_NAMES = ['Contractor bills', 'Payment file', 'Payroll for employees'];
const STEP_PILL = { todo: ['To do', 'amber'], done: ['Done', 'emerald'], idle: ['Nothing to do', 'slate'] };

function StepPanel({ n, title, state, sub, children, innerRef }) {
  const [label, t] = STEP_PILL[state] || STEP_PILL.idle;
  return (
    <section ref={innerRef} aria-labelledby={`sch-step-${n}`} className="bg-white rounded-2xl border border-slate-200/80 scroll-mt-4">
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-[13px] font-bold flex items-center justify-center flex-shrink-0" aria-hidden>{n}</span>
          <div className="min-w-0">
            <h2 id={`sch-step-${n}`} className="text-[15px] font-semibold text-slate-900 leading-6"><span className="sr-only">Step {n}: </span>{title}</h2>
            {sub && <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>}
          </div>
        </div>
        <Pill tone={t} label={label} />
      </header>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}

function PayRun({ periodStart, periods, onPeriod, bump, onChanged, onOpenProfile }) {
  const company = useLoad('/sc/hours/company', { period_start: periodStart }, { skip: !periodStart });
  const unpaid = useLoad('/sc/hours/bills', UNPAID);
  const batches = useLoad('/sc/hours/batches');
  const payroll = useLoad('/sc/hours/payroll', { period_start: periodStart }, { skip: !periodStart });
  useRefreshOn(bump, company.reload);
  useRefreshOn(bump, unpaid.reload);
  useRefreshOn(bump, batches.reload);
  useRefreshOn(bump, payroll.reload);
  const [abaPick, setAbaPick] = useState(null);
  const payRef = useRef(null);
  const payBills = useCallback((ids) => {
    setAbaPick(ids);
    try { payRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* not every browser scrolls smoothly */ }
  }, []);
  const clearAbaPick = useCallback(() => setAbaPick(null), []);

  const cRows = company.data?.rows || EMPTY;
  const contractors = cRows.filter((r) => r.engagement_type === 'contractor');
  // Bills are built from saved hours: every contractor with hours not billed yet is ready.
  const ready = contractors.filter((r) => notPaid(r.status) && r.entries > 0 && !r.bill);
  const billed = contractors.filter((r) => !!r.bill);
  const payable = (unpaid.data || EMPTY).filter((b) => b.payable);
  const openBatches = (batches.data || EMPTY).filter((b) => b.status === 'generated');
  const prRows = payroll.data?.rows || EMPTY;
  const prToSend = prRows.filter((r) => !['exported', 'paid'].includes(r.status));

  const state = [
    ready.length ? 'todo' : (billed.length ? 'done' : 'idle'),
    payable.length || openBatches.length ? 'todo' : ((batches.data || EMPTY).some((b) => b.status === 'paid') ? 'done' : 'idle'),
    prToSend.length ? 'todo' : (prRows.length ? 'done' : 'idle'),
  ];
  const firstTodo = state.indexOf('todo');
  let current = firstTodo;
  if (current < 0) current = state.some((s) => s === 'done') ? STEP_NAMES.length : -1;

  return (
    <div className="space-y-5">
      <PeriodPicker id="sch-period-pay" periods={periods} value={periodStart} onChange={onPeriod} />
      {/* The progress line; on a phone each step's own status says the same, so it steps aside. */}
      <div className="max-sm:hidden"><Steps steps={STEP_NAMES} current={current} /></div>

      <StepPanel n={1} title="Contractor bills" state={state[0]}
        sub="One bill per contractor for the pay period, one line per participant. Download it for Xero or send it straight to Xero as a draft.">
        <BillsStep periodStart={periodStart} company={company} ready={ready} billed={billed} bump={bump} onChanged={onChanged} onPay={payBills} focus={current === 0} />
      </StepPanel>

      <StepPanel n={2} title="Payment file" state={state[1]} innerRef={payRef}
        sub="An ABA file pays contractor bills from your bank. Download it here and upload it at your bank; it is never emailed.">
        <PaymentStep unpaid={unpaid} batches={batches} openBatches={openBatches} onChanged={onChanged} preselect={abaPick} onPreselectUsed={clearAbaPick} focus={current === 1} />
      </StepPanel>

      <StepPanel n={3} title="Payroll for employees" state={state[2]}
        sub="Employees are paid through payroll, never by bill or ABA file. Send their saved hours to Xero Payroll or export them.">
        <PayrollStep payroll={payroll} periodStart={periodStart} onChanged={onChanged} onOpenProfile={onOpenProfile} focus={current === 2} />
      </StepPanel>
    </div>
  );
}

// ── Step 1: contractor bills (SCP- bills; Xero CSV or straight to Xero) ───────────────────────
function BillsStep({ periodStart, company, ready, billed, bump, onChanged, onPay, focus }) {
  const [scope, setScope] = useState('period');
  const params = useMemo(() => (scope === 'period' ? { period_start: periodStart } : null), [scope, periodStart]);
  const bills = useLoad('/sc/hours/bills', params, { skip: scope === 'period' && !periodStart });
  useRefreshOn(bump, bills.reload);
  const [selected, setSelected] = useState(EMPTY);
  const [sheet, setSheet] = useState(null);
  const [confirmGen, setConfirmGen] = useState(false);
  const [result, setResult] = useState(null);
  const [resend, setResend] = useState(null);
  const [busy, setBusy] = useState('');

  const list = bills.data || EMPTY;
  const totals = list.reduce((t, b) => ({ gst: t.gst + Number(b.gst || 0), total: t.total + Number(b.total || 0) }), { gst: 0, total: 0 });
  const clearSelection = () => bills.setData((x) => (Array.isArray(x) ? [...x] : x));

  let reason = '';
  if (!ready.length) {
    if (billed.length) reason = 'Every contractor with saved hours is billed for this pay period.';
    else reason = 'No saved hours for contractors this pay period.';
  }

  const generate = async () => {
    const r = await api.post('/sc/hours/bills/generate', { period_start: periodStart });
    const res = r.data || {};
    const made = (res.results || EMPTY).filter((x) => x.ok);
    const failed = (res.results || EMPTY).filter((x) => !x.ok);
    setResult({
      title: made.length ? `${plural(made.length, 'bill')} created` : 'No bills created',
      sub: `${periodText(res.period)}. Download them for Xero, or send them to Xero as drafts.`,
      groups: [
        { tone: 'emerald', title: 'Created', items: made.map((x) => ({
          key: x.user_id, primary: `${x.bill.bill_number} · ${x.name}`,
          secondary: `${money(x.bill.total)}${x.bill.gst ? ` including ${money(x.bill.gst)} GST` : ''}${x.bill.rounding_difference ? ` · ${money(x.bill.rounding_difference)} rounding against the hour-by-hour pay` : ''}`,
        })) },
        { tone: 'red', title: 'Not created', items: failed.map((x) => ({ key: x.user_id, primary: x.name, secondary: x.error })) },
        { tone: 'slate', title: 'Skipped', items: (res.skipped || EMPTY).map((x) => ({ key: x.user_id, primary: x.name || 'Unknown', secondary: x.reason })) },
      ],
    });
    bills.reload(); company.reload(); onChanged();
  };

  const csv = async (ids) => {
    setBusy('csv');
    try {
      const name = await downloadFile('/sc/hours/bills/csv', { params: { ids: ids.join(',') } });
      toast.success(`Downloaded ${name}. Import it in Xero under Bills to pay › Import.`);
      bills.reload();
    } catch (e) { toast.error(errMsg(e, 'Could not download the Xero file.')); } finally { setBusy(''); }
  };

  const showPush = (all) => {
    if (!all.length) return;
    const failed = all.filter((x) => !x.ok);
    setResult({
      title: failed.length ? 'Some bills were not sent' : 'Sent to Xero as drafts',
      sub: 'Approve the draft bills in Xero, then pay them with a payment file.',
      groups: [
        { tone: 'emerald', title: 'Sent as drafts', items: all.filter((x) => x.ok && !x.already_sent).map((x) => ({ key: x.bill_id, primary: `${x.bill_number} · ${x.name}`, secondary: x.warning })) },
        { tone: 'violet', title: 'Already in Xero', items: all.filter((x) => x.ok && x.already_sent).map((x) => ({ key: x.bill_id, primary: `${x.bill_number} · ${x.name}` })) },
        { tone: 'red', title: 'Not sent', items: failed.map((x) => ({ key: x.bill_id, primary: x.bill_number ? `${x.bill_number} · ${x.name}` : 'Bill', secondary: x.error })) },
      ],
    });
  };
  const push = async (ids, { sendAnyway = false, earlier = EMPTY } = {}) => {
    setBusy('push');
    try {
      const r = await api.post('/sc/hours/bills/push', { ids, ...(sendAnyway ? { send_anyway: true } : {}) });
      const results = r.data?.results || EMPTY;
      const csvFirst = results.filter((x) => x.code === 'EXPORTED_AS_CSV');
      const rest = [...earlier, ...results.filter((x) => x.code !== 'EXPORTED_AS_CSV')];
      if (csvFirst.length && !sendAnyway) setResend({ items: csvFirst, earlier: rest });
      else showPush(rest);
      bills.reload();
    } catch (e) { toast.error(errMsg(e, 'Could not send the bills to Xero.')); } finally { setBusy(''); }
  };
  const cancelResend = () => { const e = resend?.earlier || EMPTY; setResend(null); showPush(e); };

  const columns = useMemo(() => [
    { id: 'number', accessorKey: 'bill_number', header: 'Bill', cell: ({ row: { original: b } }) => <RowLink onClick={() => setSheet(b)}><span className="font-mono text-[12.5px]">{b.bill_number}</span></RowLink> },
    { id: 'name', accessorKey: 'name', header: 'Contractor' },
    { id: 'period', accessorKey: 'period_end', header: 'Pay period', cell: ({ row: { original: b } }) => <span className="whitespace-nowrap">{rangeText(b.period_start, b.period_end)}</span> },
    { id: 'total', accessorKey: 'total', header: 'Total', meta: { align: 'right' }, cell: ({ row: { original: b } }) => <span><Money value={b.total} className="font-semibold text-slate-900" />{b.gst ? <span className="block text-[12px] text-slate-500">incl. {money(b.gst)} GST</span> : null}</span> },
    { id: 'status', accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <BillPill status={getValue()} /> },
    {
      id: 'pay', header: 'Payment', enableSorting: false,
      cell: ({ row: { original: b } }) => {
        if (b.paid_at) return <span className="text-[12.5px] text-emerald-700">Paid {fmtDate(b.paid_at)}</span>;
        if (b.batch_ref) return <span className="text-[12.5px] text-sky-700 font-mono">{b.batch_ref}</span>;
        if (!b.has_bank) return <Pill tone="red" size="xs" label="No bank details" />;
        if (b.bank_needs_confirmation) return <Pill tone="amber" size="xs" label="Bank change to confirm" />;
        return <span className="text-[12.5px] text-slate-500">Not paid</span>;
      },
    },
  ], []);

  const selIds = selected.map((b) => b.id);
  const payable = selected.filter((b) => b.payable).map((b) => b.id);

  return (
    <div className="space-y-4">
      {company.error ? <ErrorBlock error={company.error} onRetry={company.reload} /> : !company.data ? <LoadingBlock rows={2} /> : (
        <div className="flex flex-wrap items-start justify-between gap-4">
          {ready.length > 0 ? (
            <div className="space-y-1 min-w-0">
              <p className="text-[14px] text-slate-800">
                <span className="text-[22px] font-semibold tabular-nums text-violet-700 mr-0.5">{ready.length}</span>
                {' '}{ready.length === 1 ? 'contractor has' : 'contractors have'} saved hours ready to bill
              </p>
              <p className="text-[12.5px] text-slate-500">{ready.map((r) => r.name).join(', ')}</p>
            </div>
          ) : null}
          <GatedButton icon={Receipt} align={ready.length ? 'end' : 'start'} variant={focus ? 'primary' : 'secondary'} reason={reason} onClick={() => setConfirmGen(true)}>Generate bills</GatedButton>
        </div>
      )}

      <div className="border-t border-slate-100 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <Segmented value={scope} onChange={setScope} size="sm" options={[{ value: 'period', label: 'This pay period' }, { value: 'all', label: 'All bills' }]} />
          <p className="text-[12.5px] text-slate-500 tabular-nums">{plural(list.length, 'bill')} · {money(totals.total)}{totals.gst ? ` including ${money(totals.gst)} GST` : ''}</p>
        </div>
        <DataTable columns={columns} data={bills.data} loading={bills.loading} error={bills.error} onRetry={bills.reload}
          selectable onSelectionChange={setSelected} onRowClick={setSheet} dense
          empty={<p className="py-3 text-[13px] text-slate-500">{scope === 'period' ? 'No bills for this pay period yet.' : 'No bills yet.'}</p>} />
        {list.length > 0 && <p className="mt-2 text-[12px] text-slate-500">Choose bills to download them for Xero, send them to Xero, or pay them.</p>}
        <SelectionBar count={selected.length} onClear={clearSelection}>
          <Button size="sm" variant="secondary" icon={Download} loading={busy === 'csv'} onClick={() => csv(selIds)}>Xero CSV</Button>
          <Button size="sm" variant="secondary" icon={UploadCloud} loading={busy === 'push'} onClick={() => push(selIds)}>Send to Xero</Button>
          <Button size="sm" icon={Landmark} disabled={!payable.length} onClick={() => onPay(payable)}>Pay{payable.length && payable.length !== selected.length ? ` ${payable.length}` : ''}</Button>
        </SelectionBar>
      </div>

      <BillSheet bill={sheet} onClose={() => setSheet(null)} busy={busy}
        onCsv={(b) => csv([b.id])} onPush={(b) => push([b.id])} onPay={(b) => onPay([b.id])} />
      <DecisionModal open={confirmGen} onClose={() => setConfirmGen(false)} title="Generate contractor bills?" sub={periodText(company.data?.period)}
        confirmLabel={`Generate ${plural(ready.length, 'bill')}`} icon={Receipt} onConfirm={generate}>
        <ul className="space-y-1 text-[13px] text-slate-700">
          {ready.map((r) => <li key={r.user_id} className="flex justify-between gap-3"><span>{r.name}</span><span className="tabular-nums font-semibold">{money(r.pay_total)}</span></li>)}
        </ul>
        <p className="text-[12.5px] text-slate-500">Each bill uses the saved hours for the pay period. Time changed later goes onto the next open pay period. Employees are never billed; their hours go to payroll.</p>
      </DecisionModal>
      <DecisionModal open={!!resend} onClose={cancelResend} title="Already downloaded for Xero" width="md" variant="danger" icon={UploadCloud}
        confirmLabel="Send anyway" onConfirm={() => push((resend?.items || EMPTY).map((x) => x.bill_id), { sendAnyway: true, earlier: resend?.earlier })}>
        <Banner tone="amber" title="Check Xero first">If the file was imported, sending these again puts each bill in Xero twice.</Banner>
        <ul className="space-y-1 text-[13px] text-slate-700 list-disc pl-4">
          {(resend?.items || EMPTY).map((x) => <li key={x.bill_id}><span className="font-mono">{x.bill_number}</span> · {x.name}</li>)}
        </ul>
      </DecisionModal>
      <ResultsModal result={result} onClose={() => setResult(null)} />
    </div>
  );
}

function BillSheet({ bill, onClose, onCsv, onPush, onPay, busy }) {
  const b = bill;
  return (
    <Sheet open={!!b} onClose={onClose} width="lg" icon={Receipt} title={b ? b.bill_number : 'Bill'} sub={b ? `${b.name || ''} · ${rangeText(b.period_start, b.period_end)}` : undefined}
      footer={b && (
        <>
          <Button variant="secondary" icon={Download} loading={busy === 'csv'} onClick={() => onCsv(b)}>Xero CSV</Button>
          {!b.xero_bill_id && <Button variant="secondary" icon={UploadCloud} loading={busy === 'push'} onClick={() => onPush(b)}>Send to Xero</Button>}
          {b.payable && !b.batch_ref && !b.paid_at && <Button icon={Landmark} onClick={() => { onClose(); onPay(b); }}>Pay this bill</Button>}
        </>
      )}>
      {b && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2"><BillPill status={b.status} />{b.gst_registered && <Pill tone="slate" size="xs" label="GST registered" />}</div>
          <Facts cols={3} items={[
            ['Bill date', fmtDate(b.bill_date)], ['Due', fmtDate(b.due_date)], ['Xero contact', b.xero_contact_name || <span className="text-amber-800">Missing</span>],
            ['In Xero', b.xero_bill_id ? (b.xero_status ? words(String(b.xero_status).toLowerCase()) : 'Yes') : 'No'],
            ['Downloaded for Xero', b.exported_at ? fmtDateTime(b.exported_at) : 'No'],
            ['Payment', b.paid_at ? `Paid ${fmtDate(b.paid_at)}` : (b.batch_ref || 'Not paid')],
          ]} />
          <section>
            <SectionTitle icon={Layers}>Lines</SectionTitle>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th scope="col" className={TH}>Description</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>Hours</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>Rate</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>Amount</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>GST</th>
                  </tr>
                </thead>
                <tbody>
                  {(b.lines || EMPTY).map((l, i) => (
                    <tr key={`${l.client_id || 'x'}-${i}`}>
                      <td className={TD}>{l.description}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{Number(l.quantity).toFixed(2)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{money(l.unit_amount)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{money(l.amount)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{money(l.gst)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold text-slate-900">
                    <td colSpan={3} className={clsx(FOOT, 'text-left')}>Total</td>
                    <td className={clsx(FOOT, 'text-right')}>{money(b.subtotal)}</td>
                    <td className={clsx(FOOT, 'text-right')}>{money(b.gst)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="mt-3 text-right text-[15px] font-semibold text-slate-900 tabular-nums">Bill total {money(b.total)}</p>
          </section>
        </div>
      )}
    </Sheet>
  );
}

// ── Step 2: payment file (ABA) — choose bills, check, generate, download, mark paid ──────────
function PaymentStep({ unpaid, batches, openBatches, onChanged, preselect, onPreselectUsed, focus }) {
  const [picked, setPicked] = useState(() => new Set());
  const [date, setDate] = useState(todayIso);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmGen, setConfirmGen] = useState(false);
  const [dupOk, setDupOk] = useState(false);
  const [made, setMade] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [paidDone, setPaidDone] = useState(null);
  const [payBatch, setPayBatch] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(false);

  useEffect(() => {
    if (!preselect) return;
    setPicked(new Set(preselect)); setPreview(null); setMade(null); setPaidDone(null);
    onPreselectUsed();
  }, [preselect, onPreselectUsed]);

  const list = unpaid.data || EMPTY;
  const payable = list.filter((b) => b.payable);
  const ids = payable.filter((b) => picked.has(b.id)).map((b) => b.id);
  const pickedTotal = payable.filter((b) => picked.has(b.id)).reduce((t, b) => t + Number(b.total || 0), 0);
  const choose = (next) => { setPicked(next); setPreview(null); };
  const toggle = (id, v) => { const n = new Set(picked); if (v) n.add(id); else n.delete(id); choose(n); };
  const allOn = payable.length > 0 && ids.length === payable.length;
  const someOn = ids.length > 0 && !allOn;
  const waiting = openBatches.filter((b) => !made || b.id !== made.batch.id);

  let checkReason = '';
  if (!payable.length) checkReason = 'No unpaid contractor bills. Generate bills in step 1 first.';
  else if (!ids.length) checkReason = 'Choose the bills to pay.';
  else if (!date) checkReason = 'Choose the payment date.';

  const runPreview = async () => {
    if (!ids.length) return;
    setPreviewing(true);
    try {
      const r = await api.post('/sc/hours/aba/preview', { bill_ids: ids, payment_date: date });
      setPreview({ ...r.data, bill_ids: ids }); setDupOk(false);
    } catch (e) { toast.error(errMsg(e, 'Could not check these payments.')); } finally { setPreviewing(false); }
  };

  const generate = async () => {
    try {
      const r = await api.post('/sc/hours/aba/generate', { bill_ids: preview.bill_ids, payment_date: preview.payment_date, ...(dupOk ? { confirm: true } : {}) });
      setMade(r.data); setDownloaded(false); setPaidDone(null); setPicked(new Set()); setPreview(null);
      onChanged();
      toast.success(`Payment file ${r.data.batch.batch_ref} is ready to download`);
    } catch (e) {
      const x = e?.response?.data || {};
      if (Array.isArray(x.problems)) setPreview((p) => (p ? { ...p, problems: x.problems, can_generate: false } : p));
      if (x.code === 'POSSIBLE_DUPLICATE' && x.duplicate) setPreview((p) => (p ? { ...p, duplicate: x.duplicate } : p));
      throw e;
    }
  };

  const download = async (id) => {
    try {
      const name = await downloadFile(`/sc/hours/batches/${id}/file`);
      toast.success(`Downloaded ${name}. Upload it at your bank.`);
      if (made && made.batch.id === id) setDownloaded(true);
    } catch (e) { toast.error(errMsg(e, 'Could not download the payment file.')); }
  };

  const onPaid = (res) => {
    toast.success(`${res.batch?.batch_ref || 'Batch'} is paid: ${plural(Number(res.bills) || 0, 'bill')}, ${plural(Number(res.entries) || 0, 'time entry', 'time entries')} locked.`);
    if (made && res.batch && made.batch.id === res.batch.id) setPaidDone(res);
    onChanged();
    if (res.remittance) {
      const rs = res.remittance.results || EMPTY;
      setResult({
        title: `Remittance advice: ${res.remittance.sent} sent`,
        sub: 'Emails carry the amount, the bill lines and a remittance advice PDF. The payment file is never emailed.',
        groups: [
          { tone: 'emerald', title: 'Sent', items: rs.filter((x) => x.sent).map((x) => ({ key: x.user_id, primary: x.name })) },
          { tone: 'amber', title: 'Not sent', items: rs.filter((x) => !x.sent).map((x) => ({ key: x.user_id, primary: x.name, secondary: x.reason })) },
        ],
      });
    }
  };

  const batchCols = useMemo(() => [
    { id: 'ref', accessorKey: 'batch_ref', header: 'Batch', cell: ({ row: { original: b } }) => <RowLink onClick={() => setBatchId(b.id)}><span className="font-mono text-[12.5px]">{b.batch_ref}</span></RowLink> },
    { id: 'date', accessorKey: 'payment_date', header: 'Payment date', cell: ({ getValue }) => fmtDate(getValue()) },
    { id: 'count', accessorKey: 'payment_count', header: 'Payments', meta: { align: 'right' } },
    { id: 'total', accessorKey: 'total', header: 'Total', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="font-semibold text-slate-900" /> },
    { id: 'status', accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <BatchPill status={getValue()} /> },
    {
      id: 'paid', accessorKey: 'paid_at', header: 'Paid',
      cell: ({ row: { original: b } }) => {
        if (b.status === 'void') return <span className="text-[12.5px] text-slate-500">Voided {fmtDate(b.voided_at)}</span>;
        if (!b.paid_at) return <span className="text-slate-500">—</span>;
        return <span className="text-[12.5px]">{fmtDateTime(b.paid_at)}{b.remittance_count ? ` · ${plural(b.remittance_count, 'remittance')}` : ''}</span>;
      },
    },
  ], []);

  const allBatches = batches.data || EMPTY;

  return (
    <div className="space-y-4">
      {waiting.length > 0 && !made && (
        <ul className="space-y-2">
          {waiting.map((b) => (
            <li key={b.id} className="rounded-xl bg-sky-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-sky-900">
                  <span className="font-mono">{b.batch_ref}</span> is waiting to be marked as paid
                </p>
                <p className="text-[12.5px] text-slate-600">{plural(b.payment_count, 'payment')} · {money(b.total)} · paid on {fmtDate(b.payment_date)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" icon={Download} onClick={() => download(b.id)}>Download file</Button>
                <Button size="sm" variant={focus ? 'success' : 'secondary'} icon={BadgeCheck} onClick={() => setPayBatch(b)}>Mark as paid</Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {made ? (
        <div className="space-y-4">
          <div className="rounded-xl bg-violet-50/70 p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[12.5px] font-semibold text-violet-800">Payment file ready · <span className="font-mono">{made.batch.batch_ref}</span></p>
              <p className="mt-1 text-[28px] font-semibold tabular-nums text-slate-900 leading-tight">{money(made.batch.total)}</p>
              <p className="text-[12.5px] text-slate-600">{plural(made.batch.payment_count, 'payment')} · paid on {fmtDate(made.batch.payment_date)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="lg" variant={downloaded ? 'secondary' : 'primary'} icon={Download} onClick={() => download(made.batch.id)}>Download ABA file</Button>
              {!paidDone && <Button size="lg" variant={downloaded ? 'success' : 'secondary'} icon={BadgeCheck} onClick={() => setPayBatch(made.batch)}>Mark as paid</Button>}
            </div>
          </div>
          {paidDone ? (
            <Banner tone="emerald" title="Paid" action={<Button size="sm" variant="secondary" onClick={() => { setMade(null); setPaidDone(null); }}>Start another payment</Button>}>
              The batch, its bills and their hours are now Paid and locked.{' '}
              <button type="button" onClick={() => setBatchId(made.batch.id)}
                className="font-semibold text-emerald-900 underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                Remittance advice PDFs
              </button>
            </Banner>
          ) : (
            <>
              <Banner tone="blue" title="Next">{made.next_step || 'Download the file, upload it at your bank, check the bank shows the same number of payments and total, then mark it as paid.'}</Banner>
              <div className="text-right"><Button size="sm" variant="ghost" onClick={() => setMade(null)}>Start another payment instead</Button></div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {unpaid.error ? <ErrorBlock error={unpaid.error} onRetry={unpaid.reload} /> : !unpaid.data ? <LoadingBlock rows={3} /> : !list.length ? (
            <GatedButton align="start" variant="secondary" icon={ListChecks} reason={checkReason}>Check payments</GatedButton>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-separate border-spacing-0">
                <caption className="sr-only">Unpaid contractor bills. Choose the bills to pay.</caption>
                <thead>
                  <tr>
                    <th scope="col" className={clsx(TH, 'w-10')}>
                      <Checkbox aria-label="Choose all payable bills" checked={allOn ? true : (someOn ? 'indeterminate' : false)}
                        onChange={(v) => choose(v ? new Set(payable.map((b) => b.id)) : new Set())} disabled={!payable.length} />
                    </th>
                    <th scope="col" className={TH}>Bill</th>
                    <th scope="col" className={TH}>Contractor</th>
                    <th scope="col" className={TH}>Pay period</th>
                    <th scope="col" className={TH}>Bank details</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((b) => {
                    const on2 = picked.has(b.id);
                    return (
                      <tr key={b.id} className={clsx(on2 && 'bg-indigo-50/60')}>
                        <td className={TD}><Checkbox aria-label={`Pay ${b.bill_number}, ${b.name}`} checked={on2} disabled={!b.payable} onChange={(v) => toggle(b.id, v)} /></td>
                        <td className={clsx(TD, 'font-mono text-[12.5px]')}>{b.bill_number}</td>
                        <td className={TD}>{b.name}{!b.payable && <span className="block text-[12px] text-slate-500">Not a contractor: paid through payroll</span>}</td>
                        <td className={clsx(TD, 'whitespace-nowrap')}>{rangeText(b.period_start, b.period_end)}</td>
                        <td className={TD}>
                          {!b.has_bank ? <Pill tone="red" size="xs" label="Missing" />
                            : b.bank_needs_confirmation ? <Pill tone="amber" size="xs" label="Changed, not confirmed" />
                              : <Pill tone="emerald" size="xs" label="On file" />}
                        </td>
                        <td className={clsx(TD, 'text-right tabular-nums font-semibold text-slate-900')}>{money(b.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {list.length > 0 && (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <Field label="Payment date" hint="The day the bank processes the file. It can't be in the past.">
                  <Input type="date" value={date} min={todayIso()} onChange={(e) => { setDate(e.target.value); setPreview(null); }} className="w-[180px]" />
                </Field>
                <div className="flex flex-wrap items-start gap-3">
                  {ids.length > 0 && <p className="pt-2.5 text-[13px] text-slate-600 tabular-nums">{plural(ids.length, 'bill')} · <span className="font-semibold text-slate-900">{money(pickedTotal)}</span></p>}
                  <GatedButton icon={ListChecks} variant={focus ? 'primary' : 'secondary'} loading={previewing} reason={checkReason} onClick={runPreview}>Check payments</GatedButton>
                </div>
              </div>
              <p className="text-[12px] text-slate-500">Pay bills once they're approved in Xero. Changed bank details must be confirmed by someone with Generate pay files first.</p>
            </>
          )}

          {preview && <AbaPreview preview={preview} onGenerate={() => setConfirmGen(true)} />}
        </div>
      )}

      <div className="border-t border-slate-100 pt-3">
        <Button size="sm" variant="ghost" icon={History} onClick={() => setHistory((h) => !h)} aria-expanded={history} aria-controls="sch-pay-history">
          {history ? 'Hide payment history' : `Payment history (${allBatches.length})`}
        </Button>
        {history && (
          <div id="sch-pay-history" className="mt-2">
            <DataTable columns={batchCols} data={batches.data} loading={batches.loading} error={batches.error} onRetry={batches.reload} dense
              onRowClick={(b) => setBatchId(b.id)} empty={<p className="py-3 text-[13px] text-slate-500">No payment files yet.</p>} />
          </div>
        )}
      </div>

      <DecisionModal open={confirmGen && !!preview} onClose={() => setConfirmGen(false)} title="Generate the payment file?" width="md" icon={Landmark}
        sub={preview ? `${plural(preview.payment_count, 'payment')} · ${money(preview.total)} · paid on ${fmtDate(preview.payment_date)}` : ''}
        confirmLabel="Generate ABA file" disabled={!preview?.can_generate || (preview?.duplicate?.possible && !dupOk)} onConfirm={generate}>
        <p className="text-[13px] text-slate-600">The bills go into a payment batch. Download the file, upload it at your bank, check the bank shows the same number of payments and total, then mark the batch as paid. The file is never emailed.</p>
        {preview?.duplicate?.possible && (
          <div className="rounded-xl bg-amber-50 p-3.5 space-y-2">
            <p className="text-[13px] font-semibold text-amber-900">This looks like a repeat of {preview.duplicate.matches.map((m) => m.batch_ref).join(', ')}</p>
            <Checkbox checked={dupOk} onChange={setDupOk} label="I've checked: this is not a repeat payment" />
          </div>
        )}
      </DecisionModal>
      <MarkPaidModal batch={payBatch} onClose={() => setPayBatch(null)} onDone={onPaid} />
      <BatchSheet key={`batch:${batchId || 'closed'}`} id={batchId} onClose={() => setBatchId(null)} onDownload={download} onMarkPaid={setPayBatch}
        onVoided={() => { onChanged(); if (made && made.batch.id === batchId) setMade(null); }} />
      <ResultsModal result={result} onClose={() => setResult(null)} />
    </div>
  );
}

function AbaPreview({ preview, onGenerate }) {
  const p = preview;
  const problems = p.problems || EMPTY;
  return (
    <section aria-label="Payment check" className="space-y-4 pt-4 border-t border-slate-100">
      <Strip items={[
        { label: 'Payments', value: `${p.payment_count} · paid on ${fmtDate(p.payment_date)}` },
        { label: 'Total', value: money(p.total), tone: 'emerald' },
        problems.length ? { label: 'Checks', value: `${plural(problems.length, 'problem')} to fix`, tone: 'red' } : { label: 'Checks', value: 'All passed', tone: 'emerald' },
        { label: 'Balancing record', value: p.balancing ? 'Included (your bank needs it)' : 'Not included' },
      ]} />
      {problems.length > 0 && (
        <Banner tone="red" title="Fix these before generating the file">
          <ul className="list-disc pl-4 space-y-0.5 mt-1">{problems.map((x) => <li key={x}>{x}</li>)}</ul>
        </Banner>
      )}
      {p.duplicate?.possible && (
        <Banner tone="amber" title="Possible repeat payment">
          {p.duplicate.matches.map((m) => `${m.batch_ref} (${fmtDateTime(m.created_at)}${m.same_bills?.length ? `, holds ${plural(m.same_bills.length, 'of these bills', 'of these bills')}` : ''}${m.same_payments ? ', same payees and amounts' : ''})`).join('; ')}. Check before generating.
        </Banner>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border-separate border-spacing-0">
          <caption className="sr-only">Payments in this file</caption>
          <thead>
            <tr>
              <th scope="col" className={TH}>Contractor</th>
              <th scope="col" className={TH}>Bill</th>
              <th scope="col" className={TH}>Account name</th>
              <th scope="col" className={TH}>BSB</th>
              <th scope="col" className={TH}>Account</th>
              <th scope="col" className={TH}>Reference</th>
              <th scope="col" className={clsx(TH, 'text-right')}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(p.payments || EMPTY).map((x) => (
              <tr key={x.bill_id}>
                <td className={TD}>{x.name}{x.bank_needs_confirmation && <span className="block mt-0.5"><Pill tone="amber" size="xs" label="Bank change not confirmed" /></span>}</td>
                <td className={clsx(TD, 'font-mono text-[12.5px]')}>{x.bill_number}</td>
                <td className={TD}>{x.account_name || <span className="text-rose-700">Missing</span>}</td>
                <td className={clsx(TD, 'tabular-nums')}>{x.bsb || <span className="text-rose-700">Missing</span>}</td>
                <td className={clsx(TD, 'tabular-nums')}>{x.account_masked || <span className="text-rose-700">Missing</span>}</td>
                <td className={clsx(TD, 'font-mono text-[12px]')}>{x.lodgement_ref}</td>
                <td className={clsx(TD, 'text-right tabular-nums font-semibold text-slate-900')}>{money(x.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <GatedButton icon={Landmark} reason={p.can_generate ? '' : 'Fix the problems above first.'} onClick={onGenerate}>Generate ABA file</GatedButton>
      </div>
    </section>
  );
}

function MarkPaidModal({ batch, onClose, onDone }) {
  const [date, setDate] = useState('');
  const [remit, setRemit] = useState(false);
  useEffect(() => { if (batch) { setDate(batch.payment_date || todayIso()); setRemit(false); } }, [batch]);
  const go = async () => {
    const r = await api.post(`/sc/hours/batches/${batch.id}/paid`, { payment_date: date || undefined, send_remittance: remit });
    onDone(r.data || {});
  };
  return (
    <DecisionModal open={!!batch} onClose={onClose} title={`Mark ${batch?.batch_ref || 'this batch'} as paid?`} sub="Do this once the bank has processed the payment."
      confirmLabel="Mark as paid" variant="success" icon={BadgeCheck} onConfirm={go} width="md">
      <Facts items={[['Payments', batch?.payment_count ?? '—'], ['Total', money(batch?.total)]]} />
      <Field label="Payment date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Switch label="Email remittance advice" checked={remit} onChange={setRemit}
        hint="Each contractor with a remittance email gets the amount and their bill lines, with a remittance advice PDF. The payment file itself is never emailed." />
      <p className="text-[12.5px] text-slate-500">The batch, its bills and the hours on them change to Paid and lock.</p>
    </DecisionModal>
  );
}

function BatchSheet({ id, onClose, onDownload, onMarkPaid, onVoided }) {
  const d = useLoad(id ? `/sc/hours/batches/${id}` : null, null, { skip: !id });
  const { reload } = d;
  const [voiding, setVoiding] = useState(false);
  const [pdfFor, setPdfFor] = useState(null);
  const b = id ? d.data : null;
  const paid = !!b && b.status === 'paid';
  // One contractor's remittance advice (company letterhead, their lines, the account by its last 3
  // digits only), for a paid batch.
  const remittancePdf = async (i) => {
    setPdfFor(i.user_id);
    try { const name = await downloadFile(`/sc/hours/batches/${id}/remittance/${i.user_id}.pdf`); toast.success(`Downloaded ${name}`); }
    catch (e) { toast.error(errMsg(e, 'The remittance advice could not be made. Try again.')); }
    finally { setPdfFor(null); }
  };
  const voidIt = async (reason) => {
    const r = await api.post(`/sc/hours/batches/${id}/void`, { reason });
    toast.success(r.data?.already ? 'This batch was already void.' : `Voided. ${plural(Number(r.data?.bills_released) || 0, 'bill')} can be paid in a new file.`);
    reload(); onVoided();
  };
  return (
    <>
      <Sheet open={!!id} onClose={onClose} width="lg" icon={Landmark} title={b ? b.batch_ref : 'Payment batch'} sub={b ? `${plural(b.payment_count, 'payment')} · paid on ${fmtDate(b.payment_date)}` : undefined}
        footer={b && (
          <>
            {b.status === 'generated' && <Button variant="secondary" icon={Ban} onClick={() => setVoiding(true)}>Void batch</Button>}
            {b.status !== 'void' && b.file_valid && <Button variant="secondary" icon={Download} onClick={() => onDownload(b.id)}>Download ABA file</Button>}
            {b.status === 'generated' && b.file_valid && <Button variant="success" icon={BadgeCheck} onClick={() => onMarkPaid(b)}>Mark as paid</Button>}
            {b.status !== 'generated' && <Button variant="secondary" onClick={onClose}>Close</Button>}
          </>
        )}>
        {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !b ? <LoadingBlock rows={5} /> : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2"><BatchPill status={b.status} /><span className="text-[12.5px] text-slate-500">Payment files are downloaded only, never emailed.</span></div>
            {!b.file_valid && b.status !== 'void' && <Banner tone="red" title="Don't use this file">The stored file no longer matches the one generated. Void the batch and generate a new one.</Banner>}
            {b.status === 'void' && <Banner tone="blue" title="Voided">“{b.void_reason}” · {fmtDateTime(b.voided_at)}. Its bills went back to unpaid.</Banner>}
            {b.duplicate_of && <Banner tone="amber" title="Generated as a possible repeat">Someone confirmed it was not a repeat payment when it was generated.</Banner>}
            <Facts cols={3} items={[
              ['Payments', b.payment_count],
              ['Total', <Money key="t" value={b.total} className="font-semibold" />],
              ['Generated', `${fmtDateTime(b.created_at)}${b.created_by_name ? ` · ${b.created_by_name}` : ''}`],
              ['Paid', b.paid_at ? `${fmtDateTime(b.paid_at)}${b.paid_by_name ? ` · ${b.paid_by_name}` : ''}` : 'Not yet'],
              ['Remittance advice', b.remittance_count ? `${b.remittance_count} sent ${fmtDateTime(b.remittance_sent_at)}` : 'None sent'],
              ['File', <span key="f" className="font-mono text-[12px] break-all">{b.file_name}</span>],
              ['SHA-256', <span key="h" className="font-mono text-[12px] break-all text-slate-500">{b.file_sha256 || '—'}</span>],
            ]} />
            <section>
              <SectionTitle icon={Users}>Payments</SectionTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th scope="col" className={TH}>Contractor</th>
                      <th scope="col" className={TH}>Bill</th>
                      <th scope="col" className={TH}>BSB</th>
                      <th scope="col" className={TH}>Account</th>
                      <th scope="col" className={TH}>Reference</th>
                      <th scope="col" className={TH}>Remittance email</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Amount</th>
                      {paid && <th scope="col" className={TH}><span className="sr-only">Remittance advice</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(b.items || EMPTY).map((i) => (
                      <tr key={i.bill_id}>
                        <td className={TD}>{i.name}<span className="block text-[12px] text-slate-500">{i.account_name}</span></td>
                        <td className={clsx(TD, 'font-mono text-[12.5px]')}>{i.bill_number}</td>
                        <td className={clsx(TD, 'tabular-nums')}>{i.bsb}</td>
                        <td className={clsx(TD, 'tabular-nums')}>{i.account_masked}</td>
                        <td className={clsx(TD, 'font-mono text-[12px]')}>{i.lodgement_ref}</td>
                        <td className={TD}>{i.has_remittance_email ? 'On file' : <span className="text-slate-500">None</span>}</td>
                        <td className={clsx(TD, 'text-right tabular-nums font-semibold text-slate-900')}>{money(i.amount)}</td>
                        {paid && (
                          <td className={clsx(TD, 'text-right')}>
                            <Button size="sm" variant="secondary" icon={Download} loading={pdfFor === i.user_id} onClick={() => remittancePdf(i)}
                              aria-label={`Remittance PDF for ${i.name}`}>Remittance PDF</Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </Sheet>
      <ReasonModal open={voiding} onClose={() => setVoiding(false)} danger confirmLabel="Void batch" title={`Void ${b?.batch_ref || 'this batch'}?`}
        sub="Only void a file the bank has not paid. Its bills go back to unpaid so they can be paid in a new file. Don't upload this file afterwards."
        onConfirm={voidIt} />
    </>
  );
}

// ── Step 3: payroll — employees' saved hours to Xero Payroll or a file ────────────────────────
function PayrollStep({ payroll, periodStart, onChanged, onOpenProfile, focus }) {
  const d = payroll;
  const [selected, setSelected] = useState(EMPTY);
  const [format, setFormat] = useState('xlsx');
  const [confirm, setConfirm] = useState(null);
  const [result, setResult] = useState(null);
  const data = d.data;
  const rows = data?.rows || EMPTY;
  const scope = selected.length ? selected : rows;
  const toExport = scope.filter((r) => r.status !== 'paid');
  const toPush = scope.filter((r) => !['exported', 'paid'].includes(r.status));
  const userIds = selected.length ? selected.map((r) => r.user_id) : null;
  const hours = rows.reduce((t, r) => t + (Number(r.paid_hours) || 0), 0);
  const waiting = rows.filter((r) => !['exported', 'paid'].includes(r.status)).length;

  const doExport = async () => {
    const name = await downloadFile('/sc/hours/payroll/export', {
      method: 'post', data: { period_start: periodStart, format, ...(userIds ? { user_ids: userIds } : {}) },
    });
    toast.success(`Downloaded ${name}. The hours are now Exported to payroll.`);
    onChanged();
  };
  const doPush = async () => {
    const r = await api.post('/sc/hours/payroll/push', { period_start: periodStart, ...(userIds ? { user_ids: toPush.map((x) => x.user_id) } : {}) });
    const rs = r.data?.results || EMPTY;
    setResult({
      title: rs.some((x) => !x.ok) ? 'Some timesheets were not sent' : 'Sent to Xero Payroll',
      sub: 'Timesheets go to Xero as drafts. Approve them in Xero before the pay run.',
      groups: [
        { tone: 'emerald', title: 'Draft timesheet created', items: rs.filter((x) => x.ok).map((x) => ({ key: x.user_id, primary: x.name })) },
        { tone: 'red', title: 'Not sent', items: rs.filter((x) => !x.ok).map((x) => ({ key: x.user_id, primary: x.name, secondary: x.error })) },
      ],
    });
    onChanged();
  };

  const columns = useMemo(() => [
    {
      id: 'name', accessorKey: 'name', header: 'Employee',
      cell: ({ row: { original: r } }) => (onOpenProfile ? <RowLink onClick={() => onOpenProfile(r.user_id)}>{r.name}</RowLink> : <span className="font-semibold text-slate-900">{r.name}</span>),
    },
    { id: 'xname', accessorKey: 'xero_employee_name', header: 'Xero employee', cell: ({ getValue }) => getValue() || <Pill tone="amber" size="xs" label="Missing" /> },
    { id: 'rate', accessorKey: 'xero_earnings_rate', header: 'Earnings rate', cell: ({ getValue }) => getValue() || <Pill tone="amber" size="xs" label="Missing" /> },
    { id: 'hours', accessorKey: 'paid_hours', header: 'Paid hours', meta: { align: 'right' }, cell: ({ getValue }) => <span className="font-semibold text-slate-900">{hrs(getValue())}</span> },
    { id: 'status', accessorKey: 'status', header: 'Pay status', cell: ({ row: { original: r } }) => <EntryPill status={r.status} label={r.status_label} /> },
    {
      id: 'sent', accessorKey: 'payroll_exported_at', header: 'Sent to payroll',
      cell: ({ row: { original: r } }) => (r.payroll_exported_at
        ? <span className="text-[12.5px]">{fmtDateTime(r.payroll_exported_at)}{r.xero_timesheet_id ? ' · Xero timesheet' : ' · file'}</span>
        : <span className="text-slate-500">—</span>),
    },
    { id: 'warn', header: <span className="sr-only">Set-up problems</span>, enableSorting: false, cell: ({ row: { original: r } }) => <Warnings list={r.warnings} /> },
  ], [onOpenProfile]);

  let exportReason = '';
  if (!toExport.length) exportReason = 'No saved employee hours this pay period.';
  let pushReason = '';
  if (!toPush.length) pushReason = rows.length ? 'Everyone here is already sent to payroll.' : 'No saved employee hours this pay period.';
  else if (data && !data.xero_connected) pushReason = 'Xero isn’t connected. Export the file instead.';
  const sameReason = !!exportReason && exportReason === pushReason;

  if (d.error) return <ErrorBlock error={d.error} onRetry={d.reload} />;
  if (!data) return <LoadingBlock rows={4} />;
  return (
    <div className="space-y-4">
      {!data.xero_connected && rows.length > 0 && (
        <Banner tone="blue" title="Xero isn't connected">Export the hours for payroll entry, or connect Xero in Settings › Integrations to send timesheets straight to Xero Payroll.</Banner>
      )}
      {rows.length > 0 && (
        <Strip items={[
          { label: 'Employees', value: rows.length },
          { label: 'Paid hours', value: hrs(hours) },
          waiting ? { label: 'Not yet sent', value: waiting, tone: 'amber' } : { label: 'Not yet sent', value: 'All sent', tone: 'emerald' },
        ]} />
      )}
      {rows.length > 0 && (
        <DataTable columns={columns} data={data.rows} loading={d.loading} selectable onSelectionChange={setSelected} getRowId={(r) => r.submission_id || r.user_id} dense />
      )}
      {sameReason ? (
        <div className="flex flex-col items-start gap-1">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon={Download} disabled aria-describedby="sch-payroll-reason">Export all</Button>
            <Button variant="secondary" icon={UploadCloud} disabled aria-describedby="sch-payroll-reason">Send to Xero Payroll</Button>
          </div>
          <p id="sch-payroll-reason" className="text-[12px] text-slate-500">{exportReason} Employees’ hours show here as soon as they’re saved.</p>
        </div>
      ) : (
        <div className="flex flex-wrap items-start justify-end gap-3 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-semibold text-slate-600" id="sch-payroll-format">File type</span>
            <div role="group" aria-labelledby="sch-payroll-format"><Segmented value={format} onChange={setFormat} size="sm" options={[{ value: 'xlsx', label: 'Excel' }, { value: 'csv', label: 'CSV' }]} /></div>
          </div>
          <GatedButton variant={focus && !!pushReason ? 'primary' : 'secondary'} icon={Download} reason={exportReason} onClick={() => setConfirm('export')}>
            {selected.length ? `Export ${selected.length} selected` : 'Export all'}
          </GatedButton>
          <GatedButton variant={focus && !pushReason ? 'primary' : 'secondary'} icon={UploadCloud} reason={pushReason} onClick={() => setConfirm('push')}>Send to Xero Payroll</GatedButton>
        </div>
      )}
      <DecisionModal open={confirm === 'export'} onClose={() => setConfirm(null)} title={`Export payroll hours (${format === 'csv' ? 'CSV' : 'Excel'})?`}
        sub={periodText(data.period)} confirmLabel="Download file" icon={Download} onConfirm={doExport}>
        <p className="text-[13px] text-slate-600">{plural(toExport.length, 'employee')}: hours per day with the Xero employee and earnings rate names. Downloading hands the hours to payroll, so they change to Exported to payroll.</p>
      </DecisionModal>
      <DecisionModal open={confirm === 'push'} onClose={() => setConfirm(null)} title="Send to Xero Payroll?" sub={periodText(data.period)}
        confirmLabel={`Send ${plural(toPush.length, 'timesheet')}`} icon={UploadCloud} onConfirm={doPush}>
        <p className="text-[13px] text-slate-600">One draft timesheet per employee. Xero refuses a timesheet when the employee or earnings rate name doesn't match, the pay period doesn't line up with their Xero pay calendar, or they already have a timesheet for the period. Each refusal is listed with its reason; export those instead.</p>
      </DecisionModal>
      <ResultsModal result={result} onClose={() => setResult(null)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Coordinators: profiles, rates and bank details (shown in Settings › Coordinators)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
function setupIssues(c) {
  const out = [];
  if (!c.has_profile) out.push('No profile: paid as an employee');
  if (c.current_rate == null) out.push('No pay rate');
  if (c.engagement_type === 'contractor') {
    if (!c.contractor_code) out.push('Contractor code missing');
    if (!c.xero_contact_name) out.push('Xero contact name missing');
    if (!c.has_bank) out.push('Bank details missing');
    else if (c.bank_needs_confirmation) out.push('Bank change not confirmed');
  } else if (c.has_profile && (!c.xero_employee_name || !c.xero_earnings_rate)) out.push('Xero Payroll names missing');
  return out;
}

// The coordinator list with its profile sheet. The sheet follows ?coordinator= so a link (or a
// notification) can open one profile directly.
export function CoordinatorsPanel() {
  const [sp, setSp] = useSearchParams();
  const me = useLoad('/sc/me');
  const perms = useMemo(() => me.data?.permissions || EMPTY, [me.data]);
  const has = useCallback((...keys) => keys.some((k) => perms.includes(k)), [perms]);
  const allowed = has(...PROFILE_PERMS);
  const [bump, setBump] = useState(0);
  const changed = useCallback(() => setBump((b) => b + 1), []);
  const coordinatorId = sp.get('coordinator') || '';
  const setCoordinator = useCallback((id) => setSp((p) => {
    const n = new URLSearchParams(p);
    if (id) n.set('coordinator', id); else n.delete('coordinator');
    return n;
  }, { replace: true }), [setSp]);
  const close = useCallback(() => setCoordinator(''), [setCoordinator]);

  if (me.error) return <ErrorBlock error={me.error} onRetry={me.reload} />;
  if (!me.data) return <LoadingBlock rows={5} />;
  if (!allowed) {
    return (
      <Panel>
        <Empty icon={Lock} title="You can't see coordinator profiles">Ask an admin for the Admin, Bank details or Generate pay files permission.</Empty>
      </Panel>
    );
  }
  return (
    <>
      <CoordinatorList bump={bump} onOpen={setCoordinator} />
      <ProfileSheet key={`profile:${coordinatorId || 'closed'}`} userId={coordinatorId} onClose={close} has={has} meId={me.data.userId} onChanged={changed} />
    </>
  );
}

function CoordinatorList({ bump, onOpen }) {
  const [scope, setScope] = useState('sc');
  const list = useLoad('/sc/hours/coordinators', scope === 'all' ? ALL_STAFF : null);
  useRefreshOn(bump, list.reload);
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = list.data || EMPTY;
    return s ? all.filter((c) => `${c.name} ${c.email || ''} ${c.contractor_code || ''}`.toLowerCase().includes(s)) : all;
  }, [list.data, q]);
  const counts = useMemo(() => {
    const all = list.data || EMPTY;
    return {
      contractors: all.filter((c) => c.engagement_type === 'contractor').length,
      employees: all.filter((c) => c.engagement_type !== 'contractor').length,
      confirm: all.filter((c) => c.bank_needs_confirmation).length,
      issues: all.filter((c) => setupIssues(c).length).length,
    };
  }, [list.data]);

  const columns = useMemo(() => [
    {
      id: 'name', accessorKey: 'name', header: 'Name',
      cell: ({ row: { original: c } }) => {
        const n = nameParts(c.name);
        return (
          <div className="flex items-center gap-2.5 min-w-[190px]">
            <Avatar first={n.first} last={n.last} />
            <div className="min-w-0">
              <RowLink onClick={() => onOpen(c.user_id)}>{c.name}</RowLink>
              <p className="text-[12px] text-slate-500 truncate">{c.email}</p>
            </div>
          </div>
        );
      },
    },
    { id: 'eng', accessorKey: 'engagement_type', header: 'Paid as', cell: ({ getValue }) => <EngagementPill type={getValue()} /> },
    { id: 'rate', accessorKey: 'current_rate', header: 'Current rate', meta: { align: 'right' }, cell: ({ getValue }) => (getValue() == null ? <span className="text-slate-500">None</span> : `${money(getValue())}/h`) },
    {
      id: 'bank', header: 'Bank details', enableSorting: false,
      cell: ({ row: { original: c } }) => {
        if (c.engagement_type !== 'contractor') return <span className="text-[12.5px] text-slate-500">Not needed (payroll)</span>;
        if (!c.has_bank) return <Pill tone="red" size="xs" label="Missing" />;
        if (c.bank_needs_confirmation) return <Pill tone="amber" size="xs" label="Change to confirm" />;
        return <Pill tone="emerald" size="xs" label="Confirmed" />;
      },
    },
    {
      id: 'setup', header: 'Set-up', enableSorting: false,
      cell: ({ row: { original: c } }) => {
        const issues = setupIssues(c);
        return issues.length
          ? <span className="text-[12.5px] text-amber-800">{issues.join(' · ')}</span>
          : <Pill tone="emerald" size="xs" label="Ready" />;
      },
    },
  ], [onOpen]);

  const allClear = !counts.confirm && !counts.issues;
  return (
    <Panel title="Coordinators" icon={Users} pad={false}
      sub="How each support coordinator is paid: their engagement, hourly rate and, for contractors, Xero and bank details.">
      <div className="px-5 pb-3 space-y-4">
        {list.data && (
          <Strip items={[
            { label: 'Contractors', value: counts.contractors },
            { label: 'Employees', value: counts.employees },
            allClear ? { label: 'Set-up', value: 'All clear', tone: 'emerald' } : null,
            counts.confirm ? { label: 'Bank changes to confirm', value: counts.confirm, tone: 'amber' } : null,
            counts.issues ? { label: 'Need set-up', value: counts.issues, tone: 'amber' } : null,
          ]} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmented value={scope} onChange={setScope} size="sm" options={[{ value: 'sc', label: 'Support coordinators' }, { value: 'all', label: 'All staff' }]} />
          <SearchInput value={q} onChange={setQ} placeholder="Search by name, email or code" className="w-full sm:w-72" />
        </div>
      </div>
      <div className="px-1 pb-2">
        <DataTable columns={columns} data={rows} loading={list.loading} error={list.error} onRetry={list.reload} getRowId={(c) => c.user_id}
          onRowClick={(c) => onOpen(c.user_id)}
          empty={<Empty icon={Users} title={q ? 'No one matches' : 'No support coordinators yet'}>{q ? 'Try another name.' : 'Choose All staff to set someone up as a support coordinator.'}</Empty>} />
      </div>
    </Panel>
  );
}

const pickProfile = (p) => ({
  engagement_type: p?.engagement_type || 'employee', contractor_code: p?.contractor_code || '', abn: p?.abn || '',
  business_name: p?.business_name || '', gst_registered: !!p?.gst_registered, xero_contact_name: p?.xero_contact_name || '',
  remittance_email: p?.remittance_email || '', xero_employee_name: p?.xero_employee_name || '', xero_earnings_rate: p?.xero_earnings_rate || '',
});

function ProfileSheet({ userId, onClose, has, meId, onChanged }) {
  const p = useLoad(userId ? `/sc/hours/profiles/${userId}` : null, null, { skip: !userId });
  const canRates = !!userId && (userId === meId || has('admin', 'view_all_hours', 'generate_pay_files'));
  const rates = useLoad(userId ? `/sc/hours/profiles/${userId}/rates` : null, null, { skip: !canRates });
  const { reload: reloadProfile } = p;
  const { reload: reloadRates } = rates;
  const data = userId ? p.data : null;
  const profile = data?.profile;
  const after = useCallback(() => { reloadProfile(); reloadRates(); onChanged(); }, [reloadProfile, reloadRates, onChanged]);

  return (
    <Sheet open={!!userId} onClose={onClose} width="lg" icon={UserCog} title={data?.coordinator?.name || 'Support coordinator'} sub={data?.coordinator?.email}>
      {p.error ? <ErrorBlock error={p.error} onRetry={reloadProfile} /> : !data ? <LoadingBlock rows={6} /> : (
        <div className="space-y-8">
          <div className="flex flex-wrap items-center gap-2">
            <EngagementPill type={profile.engagement_type} />
            <span className="text-[12.5px] text-slate-500">{profile.engagement_type === 'contractor' ? 'Paid by bill and bank transfer.' : 'Paid through payroll.'}</span>
            {data.current_rate != null && <Pill tone="emerald" size="xs" label={`${money(data.current_rate)}/h now`} />}
          </div>
          {!data.exists && <Banner tone="amber" title="No profile yet">Until a profile is saved, this person is paid as an employee through payroll.</Banner>}
          <ProfileDetails userId={userId} profile={profile} canEdit={has('admin')} onSaved={after} />
          <RatesSection userId={userId} rates={rates} canSee={canRates} canAdd={has('admin')} onAdded={after} />
          {profile.engagement_type === 'contractor' && <BankSection userId={userId} data={data} has={has} onChanged={after} />}
        </div>
      )}
    </Sheet>
  );
}

function ProfileDetails({ userId, profile, canEdit, onSaved }) {
  const init = useMemo(() => pickProfile(profile), [profile]);
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setF(init); setError(''); }, [init]);
  const dirty = JSON.stringify(f) !== JSON.stringify(init);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v && v.target ? v.target.value : v }));
  const contractor = f.engagement_type === 'contractor';

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.put(`/sc/hours/profiles/${userId}`, { ...f, contractor_code: f.contractor_code.trim().toUpperCase() });
      toast.success('Profile saved');
      onSaved();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <section>
        <SectionTitle icon={UserCog}>Details</SectionTitle>
        <Facts cols={2} items={contractor ? [
          ['Contractor code', profile.contractor_code || '—'], ['ABN', profile.abn || '—'], ['Business name', profile.business_name || '—'],
          ['GST registered', profile.gst_registered ? 'Yes' : 'No'], ['Xero contact name', profile.xero_contact_name || '—'], ['Remittance email', profile.remittance_email || '—'],
        ] : [
          ['Xero Payroll employee', profile.xero_employee_name || '—'], ['Xero earnings rate', profile.xero_earnings_rate || '—'],
        ]} />
        <p className="mt-3 text-[12px] text-slate-500">Only someone with the Admin permission can change these.</p>
      </section>
    );
  }

  return (
    <section>
      <SectionTitle icon={UserCog}>Details</SectionTitle>
      <form onSubmit={save} className="space-y-4">
        <Field label="Paid as" hint="Contractors are paid by bill and bank transfer; employees through payroll.">
          <Select value={f.engagement_type} onChange={set('engagement_type')}
            options={[{ value: 'employee', label: 'Employee', hint: 'Hours go to Xero Payroll or a payroll file' }, { value: 'contractor', label: 'Contractor', hint: 'Paid by bill and bank transfer' }]} />
        </Field>
        {contractor ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contractor code" required hint="Letters and numbers, used in their bill numbers.">
              <Input value={f.contractor_code} onChange={set('contractor_code')} maxLength={20} autoCapitalize="characters" className="uppercase font-mono" />
            </Field>
            <Field label="ABN" hint="11 digits. Needed when GST registered."><Input inputMode="numeric" value={f.abn} onChange={set('abn')} maxLength={14} /></Field>
            <Field label="Business name"><Input value={f.business_name} onChange={set('business_name')} maxLength={200} /></Field>
            <Field label="Xero contact name" hint="Exactly as the contact is named in Xero."><Input value={f.xero_contact_name} onChange={set('xero_contact_name')} maxLength={200} /></Field>
            <Field label="Remittance email" hint="Where remittance advice goes after payment."><Input type="email" value={f.remittance_email} onChange={set('remittance_email')} /></Field>
            <div className="sm:pt-6"><Switch label="GST registered" hint="GST is added to their bills." checked={f.gst_registered} onChange={set('gst_registered')} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Xero Payroll employee name" hint="As the employee is named in Xero Payroll."><Input value={f.xero_employee_name} onChange={set('xero_employee_name')} maxLength={200} /></Field>
            <Field label="Xero earnings rate name" hint="An hourly earnings rate in Xero Payroll."><Input value={f.xero_earnings_rate} onChange={set('xero_earnings_rate')} maxLength={120} /></Field>
          </div>
        )}
        {error && <Banner tone="red" title="Not saved">{error}</Banner>}
        <div className="flex items-center justify-end gap-2 pt-1">
          {dirty && <span className="text-[12px] text-amber-800 font-medium">Unsaved changes</span>}
          <Button type="submit" loading={busy} disabled={!dirty}>Save details</Button>
        </div>
      </form>
    </section>
  );
}

function RatesSection({ userId, rates, canSee, canAdd, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState('');
  useEffect(() => { if (adding) { setRate(''); setFrom(todayIso()); } }, [adding]);
  const add = async () => {
    if (!(Number(rate) > 0)) throw new Error('Enter the hourly rate in dollars (more than $0).');
    if (!from) throw new Error('Choose the date the rate starts.');
    await api.post(`/sc/hours/profiles/${userId}/rates`, { rate: Number(rate), effective_from: from });
    toast.success(`Rate ${money(Number(rate))}/h from ${fmtDate(from)} added`);
    onAdded();
  };
  return (
    <section>
      <SectionTitle icon={Wallet} action={canAdd && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>Add rate</Button>}>Hourly pay rate</SectionTitle>
      {!canSee ? <p className="text-[13px] text-slate-500">You need the View all hours permission to see pay rates.</p>
        : rates.error ? <ErrorBlock error={rates.error} onRetry={rates.reload} />
          : !rates.data ? <LoadingBlock rows={2} />
            : !rates.data.length ? <Empty icon={Wallet} title="No pay rate yet">Hours without a rate are left out of pay, and a contractor’s bill can’t be generated until a rate covers their hours.</Empty> : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] border-separate border-spacing-0">
                  <caption className="sr-only">Rate history, newest first</caption>
                  <thead>
                    <tr>
                      <th scope="col" className={TH}>Starts</th>
                      <th scope="col" className={clsx(TH, 'text-right')}>Rate</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={TH}>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.data.map((x) => (
                      <tr key={x.id}>
                        <td className={TD}>{fmtDate(x.effective_from)}</td>
                        <td className={clsx(TD, 'text-right tabular-nums font-semibold text-slate-900')}>{money(x.rate)}/h</td>
                        <td className={TD}>{x.current ? <Pill tone="emerald" size="xs" label="Current" /> : x.future ? <Pill tone="blue" size="xs" label="Upcoming" /> : <Pill tone="slate" size="xs" label="Earlier" />}</td>
                        <td className={clsx(TD, 'text-[12.5px] text-slate-500')}>{fmtDateTime(x.created_at)}{x.created_by_name ? ` · ${x.created_by_name}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      <p className="mt-2 text-[12px] text-slate-500">Rates are never overwritten. Each entry is paid at the rate in force on its date.</p>
      <DecisionModal open={adding} onClose={() => setAdding(false)} title="Add an hourly rate" sub="The new rate applies to entries on and after its start date."
        confirmLabel="Add rate" icon={Plus} onConfirm={add}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rate per hour ($)" required><Input type="number" inputMode="decimal" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} /></Field>
          <Field label="Starts on" required><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        </div>
        <p className="text-[12.5px] text-slate-500">A start date inside hours that are already billed, exported or paid is refused: start the rate after them.</p>
      </DecisionModal>
    </section>
  );
}

function BankSection({ userId, data, has, onChanged }) {
  const canBank = has('bank_details');
  const canConfirm = has('generate_pay_files');
  const profile = data.profile;
  const bank = data.bank || null;
  const needsConfirm = !!profile.bank_needs_confirmation;
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [form, setForm] = useState({ account_name: '', bsb: '', account_number: '', lodgement_ref: '' });

  useEffect(() => { setRevealed(null); }, [userId]);
  useEffect(() => {
    if (!revealed) return undefined;
    const t = setTimeout(() => setRevealed(null), 30000);
    return () => clearTimeout(t);
  }, [revealed]);
  useEffect(() => {
    if (editing) setForm({ account_name: bank?.account_name || '', bsb: bank?.bsb || '', account_number: '', lodgement_ref: bank?.lodgement_ref || '' });
  }, [editing, bank]);

  const reveal = async () => {
    setRevealing(true);
    try { const r = await api.post(`/sc/hours/profiles/${userId}/bank/reveal`); setRevealed(r.data?.account_number || ''); }
    catch (e) { toast.error(errMsg(e, 'Could not show the account number.')); } finally { setRevealing(false); }
  };
  const save = async () => {
    const r = await api.put(`/sc/hours/profiles/${userId}/bank`, { ...form, account_number: form.account_number.trim() || undefined });
    if (r.data?.changed) toast.success('Bank details saved. Someone with Generate pay files must confirm them before the next payment file.');
    else toast('No changes to save.');
    setRevealed(null); onChanged();
  };
  const confirmBank = async () => {
    const r = await api.post(`/sc/hours/profiles/${userId}/bank/confirm`);
    toast.success(r.data?.already ? 'These bank details were already confirmed.' : 'Bank details confirmed');
    onChanged();
  };
  const setF = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.value }));

  return (
    <section>
      <SectionTitle icon={Landmark} action={canBank && (
        <Button size="sm" variant="secondary" icon={profile.has_bank ? Pencil : Plus} onClick={() => setEditing(true)} disabled={!data.exists}>{profile.has_bank ? 'Change' : 'Add bank details'}</Button>
      )}>Bank details</SectionTitle>
      {needsConfirm && (
        <div className="mb-3">
          <Banner tone="amber" title="Changed and not confirmed"
            action={canConfirm && <Button size="sm" variant="secondary" icon={ShieldCheck} onClick={() => setConfirming(true)}>Confirm bank change</Button>}>
            {bank?.changed_at ? `Changed ${fmtDateTime(bank.changed_at)}. ` : ''}No payment file pays this contractor until someone with Generate pay files confirms the change. The person who made it can’t confirm it themselves.
          </Banner>
        </div>
      )}
      {!canBank ? (
        <p className="text-[13px] text-slate-500">
          {profile.has_bank ? 'Bank details are on file.' : 'No bank details yet.'} You need the Bank details permission to see or change them.
        </p>
      ) : !profile.has_bank || !bank ? (
        <Empty icon={Landmark} title="No bank details yet">{data.exists ? 'Add them before paying this contractor by payment file.' : 'Save the profile as a contractor first.'}</Empty>
      ) : (
        <div className="space-y-3">
          {bank.account_unreadable && <Banner tone="red" title="The stored account number can't be read">Enter the bank details again.</Banner>}
          <Facts cols={2} items={[
            ['Account name', bank.account_name || '—'],
            ['BSB', <span key="b" className="tabular-nums">{bank.bsb || '—'}</span>],
            ['Account number', (
              <span key="a" className="inline-flex items-center gap-2">
                <span className="tabular-nums font-mono" aria-live="polite">{revealed || bank.account_masked || '—'}</span>
                {revealed
                  ? <Button size="sm" variant="ghost" icon={EyeOff} onClick={() => setRevealed(null)}>Hide</Button>
                  : !bank.account_unreadable && <Button size="sm" variant="ghost" icon={Eye} loading={revealing} onClick={reveal}>Reveal</Button>}
              </span>
            )],
            ['Lodgement reference', bank.lodgement_ref || 'The bill number'],
            ['Changed', bank.changed_at ? fmtDateTime(bank.changed_at) : '—'],
            ['Confirmed', bank.confirmed_at ? fmtDateTime(bank.confirmed_at) : (needsConfirm ? <span key="c" className="text-amber-800">Not yet</span> : '—')],
          ]} />
          <p className="text-[12px] text-slate-500 flex items-center gap-1.5"><Lock size={12} aria-hidden />Shown masked. Revealing the full number is recorded in the audit log and hides again after 30 seconds.</p>
        </div>
      )}
      <DecisionModal open={editing} onClose={() => setEditing(false)} title={profile.has_bank ? 'Change bank details' : 'Add bank details'} width="md" icon={Landmark}
        sub="Every change is logged and must be confirmed by someone with Generate pay files before the next payment file." confirmLabel="Save bank details" onConfirm={save}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Account name" required hint="Up to 32 characters (the bank file limit)." className="sm:col-span-2">
            <Input value={form.account_name} onChange={setF('account_name')} maxLength={32} autoComplete="off" />
          </Field>
          <Field label="BSB" required hint="000-000"><Input inputMode="numeric" value={form.bsb} onChange={setF('bsb')} placeholder="000-000" maxLength={7} autoComplete="off" /></Field>
          <Field label="Account number" required={!profile.has_bank} hint={profile.has_bank ? `Leave blank to keep ${bank?.account_masked || 'the current number'}.` : 'Up to 9 digits.'}>
            <Input inputMode="numeric" value={form.account_number} onChange={setF('account_number')} maxLength={11} autoComplete="off" />
          </Field>
          <Field label="Lodgement reference" hint="Up to 18 characters. Blank uses the bill number." className="sm:col-span-2">
            <Input value={form.lodgement_ref} onChange={setF('lodgement_ref')} maxLength={18} autoComplete="off" />
          </Field>
        </div>
      </DecisionModal>
      <DecisionModal open={confirming} onClose={() => setConfirming(false)} title="Confirm the bank change?" icon={ShieldCheck} confirmLabel="Confirm bank details"
        sub="Check the new details with the contractor through a channel you trust (a call to a number you already have), not a reply to an email asking for the change." onConfirm={confirmBank}>
        {bank ? (
          <Facts items={[['Account name', bank.account_name || '—'], ['BSB', bank.bsb || '—'], ['Account', bank.account_masked || '—'], ['Changed', fmtDateTime(bank.changed_at)]]} />
        ) : <p className="text-[13px] text-slate-600">You can’t see the details without the Bank details permission. Confirm only if they were checked by someone who can.</p>}
      </DecisionModal>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Client level: a participant's hours, for the participant file
// ═══════════════════════════════════════════════════════════════════════════════════════════════
export function ParticipantHours({ clientId, coordinationId, participantName, budgets, services }) {
  const periods = useLoad('/sc/hours/periods', PERIOD_PARAMS);
  const me = useLoad('/sc/me');
  const [picked, setPicked] = useState('');
  const [mode, setMode] = useState('period');
  const current = (periods.data?.periods || EMPTY).find((p) => p.current);
  const start = picked || (current ? current.start : '');
  const params = useMemo(() => ({ ...(start ? { period_start: start } : {}), ...(mode === 'plan' ? { plan: '1' } : {}), ...(coordinationId ? { coordination_id: coordinationId } : {}) }), [start, mode, coordinationId]);
  const d = useLoad(clientId ? `/sc/hours/client/${clientId}` : null, params, { skip: !clientId || (!start && periods.loading) });
  const { reload } = d;
  const editor = useEntryEditor({ coordinationId, budgets, services, participantName });
  const { open: openEntry, busyId } = editor;
  const data = d.data;
  const tp = data?.totals?.pay_period || {};
  const tplan = data?.totals?.plan_period;
  const plan = data?.plan_period;
  const showingPlan = data?.showing === 'plan' && !!plan;

  // Notes in the same window that SC hours doesn't count (no start and end times, or saved before
  // SC hours started), so a 0 h total is never a silent mystery.
  const win = data ? (showingPlan ? plan : data.period) : null;
  const notesParams = useMemo(() => (win ? { client_id: clientId, from: win.start, to: win.end } : null), [win, clientId]);
  const notes = useLoad(win ? '/sc/time-entries' : null, notesParams, { skip: !win || !clientId });
  const { reload: reloadNotes } = notes;
  const refresh = useCallback(() => { reload(); reloadNotes(); }, [reload, reloadNotes]);
  useOnTimeSaved(refresh);
  const uncounted = useMemo(() => {
    if (!data || !Array.isArray(notes.data)) return null;
    const mine = data.own_only ? me.data?.userId : null;
    if (data.own_only && !mine) return null;
    const counted = new Set((data.entries || EMPTY).map((e) => e.id));
    const list = notes.data.filter((n) => !counted.has(n.id) && n.source !== 'adjustment'
      && !['cancelled', 'reverted'].includes(n.status) && (!mine || n.coordinator_id === mine));
    const untimed = list.filter((n) => !(n.start_time && n.end_time)).length;
    return { untimed, older: list.length - untimed };
  }, [data, notes.data, me.data]);

  const byActivity = useMemo(() => {
    const m = new Map();
    for (const e of data?.entries || EMPTY) {
      const k = e.activity || 'No activity type';
      const c = m.get(k) || { activity: k, entries: 0, minutes: 0, billable_minutes: 0 };
      c.entries += 1;
      c.minutes += Number(e.minutes) || 0;
      if (e.billable) c.billable_minutes += Number(e.minutes) || 0;
      m.set(k, c);
    }
    return [...m.values()].sort((a, b) => b.minutes - a.minutes);
  }, [data]);

  const entryCols = useMemo(() => [
    {
      id: 'date', accessorKey: 'date', header: 'Date',
      cell: ({ row: { original: e } }) => (
        <span className="inline-flex items-center gap-1.5">
          <RowLink onClick={() => openEntry(e)} busy={busyId === e.id} label={`Open time entry: ${fmtDate(e.date)}, ${e.coordinator_name || ''}`}>
            <span className="whitespace-nowrap">{fmtDate(e.date)}</span>
          </RowLink>
          <LockBadge entry={e} className="text-violet-500" />
        </span>
      ),
    },
    { id: 'coordinator', accessorKey: 'coordinator_name', header: 'Support coordinator', cell: ({ getValue }) => getValue() || '—' },
    {
      id: 'activity', accessorKey: 'activity', header: 'Activity',
      cell: ({ row: { original: e } }) => (e.source === 'adjustment' ? <span>Adjustment{e.activity ? ` · ${e.activity}` : ''}</span> : (e.activity || '—')),
    },
    { id: 'start', accessorKey: 'start_time', header: 'Start', cell: ({ getValue }) => <span className="tabular-nums">{getValue() || '—'}</span> },
    { id: 'end', accessorKey: 'end_time', header: 'End', cell: ({ getValue }) => <span className="tabular-nums">{getValue() || '—'}</span> },
    {
      id: 'hours', accessorKey: 'minutes', header: 'Hours', meta: { align: 'right' },
      cell: ({ getValue }) => <span className={clsx('font-semibold', Number(getValue()) < 0 ? 'text-rose-600' : 'text-slate-900')}>{signedHours(getValue())} h</span>,
    },
    { id: 'billable', accessorKey: 'billable', header: 'Billable', cell: ({ getValue }) => (getValue() ? <Pill tone="emerald" size="xs" label="Billable" /> : <Pill tone="slate" size="xs" label="Non-billable" />) },
    { id: 'pay', accessorKey: 'pay_status', header: 'Pay status', cell: ({ row: { original: e } }) => <EntryPill status={e.pay_status} label={e.pay_status_label} /> },
  ], [openEntry, busyId]);

  const coordCols = useMemo(() => [
    { id: 'name', accessorKey: 'coordinator_name', header: 'Support coordinator' },
    { id: 'entries', accessorKey: 'entries', header: 'Entries', meta: { align: 'right' } },
    { id: 'total', accessorKey: 'total_hours', header: 'Hours', meta: { align: 'right' }, cell: ({ getValue }) => <span className="font-semibold text-slate-900">{hrs(getValue())}</span> },
    { id: 'billable', accessorKey: 'billable_hours', header: 'Billable', meta: { align: 'right' }, cell: ({ getValue }) => hrs(getValue()) },
    { id: 'non', accessorKey: 'nonbillable_hours', header: 'Non-billable', meta: { align: 'right' }, cell: ({ getValue }) => hrs(getValue()) },
  ], []);
  const actCols = useMemo(() => [
    { id: 'activity', accessorKey: 'activity', header: 'Activity type' },
    { id: 'entries', accessorKey: 'entries', header: 'Entries', meta: { align: 'right' } },
    { id: 'hours', accessorKey: 'minutes', header: 'Hours', meta: { align: 'right' }, cell: ({ getValue }) => <span className="font-semibold text-slate-900">{hoursOf(getValue())} h</span> },
    { id: 'billable', accessorKey: 'billable_minutes', header: 'Billable', meta: { align: 'right' }, cell: ({ getValue }) => `${hoursOf(getValue())} h` },
  ], []);

  const showingText = showingPlan ? `Whole plan · ${rangeText(plan.start, plan.end)}` : periodText(data?.period);
  const notCountedText = (() => {
    if (!uncounted) return '';
    const parts = [];
    if (uncounted.untimed) parts.push(`${plural(uncounted.untimed, 'note')} ${uncounted.untimed === 1 ? 'has' : 'have'} no times, so ${uncounted.untimed === 1 ? 'it isn’t' : 'they aren’t'} counted here.`);
    if (uncounted.older) parts.push(`${plural(uncounted.older, 'older note')} ${uncounted.older === 1 ? 'was' : 'were'} saved before SC hours started, so ${uncounted.older === 1 ? 'it isn’t' : 'they aren’t'} counted ${uncounted.untimed ? 'either' : 'here'}.`);
    return parts.join(' ');
  })();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented value={mode} onChange={setMode} size="sm" options={[{ value: 'period', label: 'Pay period' }, { value: 'plan', label: 'Whole plan' }]} />
        {mode === 'period' && <PeriodPicker id={`sch-period-client-${clientId || 'x'}`} periods={periods} value={start} onChange={setPicked} hideLabel />}
      </div>
      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !data ? <LoadingBlock rows={5} /> : (
        <>
          {mode === 'plan' && !plan && <Banner tone="amber" title="No plan dates">This participant has no plan dates, so the whole-plan view isn’t available. Showing the pay period.</Banner>}
          {notCountedText && <Banner tone="blue">{notCountedText}</Banner>}
          <section aria-label="Hour totals" className="rounded-2xl bg-white border border-slate-200/80 px-5 py-4">
            <Strip items={[
              { label: 'This pay period', value: hrs(tp.total_hours) },
              { label: 'Whole plan', value: tplan ? hrs(tplan.total_hours) : 'No plan dates' },
              { label: 'Billable this period', value: hrs(tp.billable_hours) },
              { label: 'Non-billable this period', value: hrs(tp.nonbillable_hours) },
            ]} />
            <p className="mt-3 text-[12px] text-slate-500">
              {periodText(data.period)}{plan ? ` · plan ${rangeText(plan.start, plan.end)}` : ''}
              {data.own_only ? ` · only your entries are shown; the View all hours permission shows everyone’s for ${participantName || data.client?.name || 'this participant'}.` : ''}
            </p>
          </section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            <Panel title="By support coordinator" icon={Users} sub={showingText} pad={false}>
              <div className="px-1 pb-2">
                <DataTable dense columns={coordCols} data={data.totals?.by_coordinator} getRowId={(r) => r.coordinator_id || 'none'}
                  empty={<p className="px-4 py-3 text-[13px] text-slate-500">No time logged.</p>} />
              </div>
            </Panel>
            <Panel title="By activity type" icon={Layers} sub={showingText} pad={false}>
              <div className="px-1 pb-2">
                <DataTable dense columns={actCols} data={byActivity} getRowId={(r) => r.activity}
                  empty={<p className="px-4 py-3 text-[13px] text-slate-500">No time logged.</p>} />
              </div>
            </Panel>
          </div>
          <Panel title="Time entries" icon={Clock} sub={`${showingText} · choose an entry to open it`} pad={false}>
            <div className="px-1 pb-2">
              <DataTable columns={entryCols} data={data.entries} loading={d.loading} onRowClick={openEntry} initialSort={[{ id: 'date', desc: true }]}
                empty={<Empty icon={Clock} title="No time entries">Time logged for this participant shows here.</Empty>} />
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
